import { AES, enc, lib, MD5, SHA1, mode, pad } from 'crypto-js';
import { config, rsaEncrypt, ruleEngine, } from 'env_def';
import lodashGet from 'lodash/get';
import throttle from 'lodash/throttle';
import moment from 'moment';
import { checkMasterInfo } from '@mail/jmail';
import { api } from '@/api/api';
import { apis, configKeyStore, getUrl, inWindow, isElectron, isInMobile, isLowMemoryMode, getUrlPre, systemProxyType, defaultSystemProxyType, } from '@/config';
import { globalStoreConfig } from '@/api/data/store';
import { ListStore } from '@/api/commonModel';
import { locationHelper } from '@/api/util/location_helper';
import { emailPattern, getIn18Text, getIn18TextByLang, isWindows7 } from '@/api/utils';
import { getOs } from '../../utils/confOs';
import getUrlPathByString from '../../utils/getUrlPath';
import { SYSTEM_LANGUAGE, DEFAULT_LANG } from '../../utils/global_label/index';
import { RSASign, arrayBufferToHex } from '@/api/util/rsa_helper';
const LOW_MEMORY_MODE_KEY = 'lowMemoryModeNew';
const SYSTEM_PROXY_TYPE_STORE_KEY = 'useSystemProxyType';
const APP_LOCK_KEY = 'appLockKey';
const ZOOM_VAL_KEY = 'appPageZoomVal';
const DISABLE_ACCESS_KEY = 'disableAccessibility';
const WIN7_USE_NOTIFICATION = 'win7_use_notification';
const buildProfile = config('profile');
function isInredirectWhiteList(url) {
    const defaultValue = false;
    try {
        if (url && url.length) {
            if (url.includes('#/resetPwd') && url.includes('/static/qiyeurs/')) {
                return true;
            }
        }
        return defaultValue;
    }
    catch (ex) {
        console.warn('isInredirectWhiteList error', ex);
        return defaultValue;
    }
}
export const JsonFormatter = {
    stringify(cipherParams) {
        const jsonObj = { c: cipherParams.ciphertext.toString(enc.Base64) };
        if (cipherParams.iv) {
            jsonObj.i = cipherParams.iv.toString(enc.Base64);
        }
        if (cipherParams.salt) {
            jsonObj.s = cipherParams.salt.toString(enc.Base64);
        }
        return JSON.stringify(jsonObj);
    },
    parse(jsonStr) {
        const jsonObj = JSON.parse(jsonStr);
        if (!jsonObj.c || !jsonObj.i || !jsonObj.s) {
            throw new Error('illegal input');
        }
        const cipherParams = lib.CipherParams.create({
            ciphertext: enc.Base64.parse(jsonObj.c),
        });
        if (jsonObj.i) {
            cipherParams.iv = enc.Base64.parse(jsonObj.i);
        }
        if (jsonObj.s) {
            cipherParams.salt = enc.Base64.parse(jsonObj.s);
        }
        return cipherParams;
    },
};
const ver = config('version');
const isPord = config('stage') === 'prod';
const currentSessionNameKey = 'SessionName';
const currentSessionStoreConfig = { noneUserRelated: true };
const trackCpuMemKey = 'track_cpu_mem';
const getHashInfo = (redirectLink) => {
    var _a, _b;
    try {
        const hash = (_a = redirectLink.split('#')[1]) !== null && _a !== void 0 ? _a : '';
        const searchStr = (_b = redirectLink.split('?')[1]) !== null && _b !== void 0 ? _b : '';
        const searchParams = new URLSearchParams(searchStr);
        const json = {};
        for (const [key, value] of searchParams) {
            json[key] = value;
        }
        return {
            hash,
            searchParams,
            params: json,
        };
    }
    catch (error) {
        return undefined;
    }
};
class SystemApiImpl {
    constructor() {
        var _a;
        this.redirectUrlPrefix = new Map();
        this.apiStartTime = 0;
        this.subAccountCookies = {};
        this.contextPath = '';
        this.isMsiBuild = false;
        this.browserAgentIdStr = {
            ie: [/msie ([\d.]+);/i, 100],
            edge: [/edge?\/([\d.]+)/i, 100],
            firefox: [/firefox\/([\d.]+)/i, 100],
            chrome: [/chrome\/?([\d.]+)/i, 10],
            safari: [/safari\/([\d.]+)/i, 1],
            opera: [/opera ([\d.]+)/i, 100],
            sirius: [/sirius-desktop\/([\d.]+)/i, 1100],
            uc: [/UCBrowser\/([\d.]+)/i, 1000],
            qq: [/QQBrowser\/([\d.]+)/i, 1000],
            sogou: [/MetaSr ?([\d.]+)/i, 1000],
        };
        this._notificationAvailableLabel = 2;
        this._currentShowedNotification = 0;
        this.systemMusic = null;
        this.webmailHZHost = config('webMailHZHost');
        this.webmailBJHost = config('webMailBJHost');
        this.isMac = getOs() === 'mac';
        this.isLockingApp = null;
        this.dailyActiveApp = {
            eventPeriod: 'extLong',
            handler: async () => {
                const isMainPage = this.isMainPage();
                const currentUser = this.getCurrentUser();
                if (currentUser && !isMainPage)
                    return;
                const trackParam = (await this.getActiveUserTrackParams()) || {};
                this.dataTracker.track('pc_dailyActiveUser', Object.assign({ type: 'activeStatus' }, trackParam));
            },
            id: 'dailyActiveApp',
            seq: 0,
        };
        this.parseUrlObjectParams = (hash) => {
            const params = {};
            hash
                .replace('#', '')
                .split('&')
                .forEach(param => {
                const [key, value] = param.split('=');
                params[key] = value;
            });
            return params;
        };
        this.uniqueWinMap = new Map();
        this.getCacheRangeKey = (sizeMb) => {
            const config = [
                { val: '<100M', checkFn: (val) => val < 100 },
                { val: '<200M', checkFn: (val) => val < 200 },
                { val: '<300M', checkFn: (val) => val < 300 },
                { val: '<500M', checkFn: (val) => val < 500 },
                { val: '<1G', checkFn: (val) => val < 1024 },
                { val: '<2G', checkFn: (val) => val < 2048 },
                { val: '<5G', checkFn: (val) => val < 5120 },
                { val: '>5G', checkFn: (val) => val >= 5120 },
            ];
            if (!sizeMb || sizeMb < 0)
                return '';
            let rangeKey = '';
            for (let i = 0; i < config.length; ++i) {
                const currConfig = config[i];
                if (currConfig.checkFn(sizeMb)) {
                    rangeKey = currConfig.val;
                    break;
                }
            }
            return rangeKey;
        };
        this.contextPath = config('contextPath');
        this.isMsiBuild = ((_a = config('is_msi_build')) === null || _a === void 0 ? void 0 : _a.toString()) === 'true';
        this.name = apis.defaultSystemApiImpl;
        this.storeApi = api.getDataStoreApi();
        this.eventApi = api.getEventApi();
        this.mailConfApi = api.requireLogicalApi(apis.mailConfApiImpl);
        this.netStorageApi = api.requireLogicalApi(apis.netStorageImpl);
        this.dataTracker = api.requireLogicalApi(apis.dataTrackerApiImp);
        this.htmlApi = api.requireLogicalApi(apis.htmlApi);
        this.loggerApi = api.requireLogicalApi(apis.loggerApiImpl);
        this.httpApi = api.getDataTransApi();
        this.accountApi = api.requireLogicalApi(apis.accountApiImpl);
        this.taskCenterApi = api.requireLogicalApi(apis.taskCenterApiImpl);
        this.loginApi = api.requireLogicalApi(apis.loginApiImpl);
        this.productAuthApi = api.requireLogicalApi(apis.productAuthApiImpl);
        if (inWindow() && window.electronLib) {
            this.winMap = {};
            this.getCurrentWinInfo().then();
        }
        this.cookies = {};
        this.handleInternalUrlWebJump = this.handleInternalUrlWebJump.bind(this);
        const diskDetailPageHandler = {
            handleUrl: this.handleInternalUrlWebJump,
            openInElectron: true,
            needTransferUseApi: false,
            needUploadAccountInfo: false,
            openInternalPage: true,
        };
        const diskFolderAnchorPageHandler = {
            handleUrl: this.handleDiskShareWebJump.bind(this),
            openInElectron: true,
            needTransferUseApi: false,
            needUploadAccountInfo: false,
            openInternalPage: true,
        };
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/share/', diskFolderAnchorPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/sheet', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/doc', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/unitable', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/resources', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/share/', diskFolderAnchorPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/sheet', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/doc', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/resources', diskDetailPageHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.hostForTest + '/#disk', {
            handleUrl: this.handleDiskPageRouteJump.bind(this),
        });
        const mailPageJumpHandler = {
            handleUrl: this.handleMailReadingPageJump.bind(this),
        };
        const mailTeamPageJumpHandler = {
            handleUrl: this.handleMailReadingOnlyPageJump.bind(this),
        };
        this.redirectUrlPrefix.set('https://sirius.qiye.163.com/router/mail/detail', mailPageJumpHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/in-team-detail', mailTeamPageJumpHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/detail', mailPageJumpHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/mailsetting', {
            handleUrl: this.handleSettingWebJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/calendar/home', {
            handleUrl: this.handleScheduleJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/calendar/jump_schedule_detail', {
            handleUrl: this.handleScheduleJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://sirius.qiye.163.com/router/mail/detail', mailPageJumpHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/in-team-detail', mailTeamPageJumpHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/detail', mailPageJumpHandler);
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/it-notify-manage/api/pub/notify/visit', {
            handleUrl: (url) => {
                const { searchParams } = new URL(url);
                const redirect = searchParams.get('redirect') || '';
                const sign = searchParams.get('sign') || '';
                const hashInfo = getHashInfo(redirect);
                if (!redirect)
                    return '';
                if (sign) {
                    this.httpApi.get(SystemApiImpl.host + '/it-notify-manage/api/pub/notify/linkLog', { sign }).catch(() => { });
                }
                if (!redirect.startsWith(SystemApiImpl.host))
                    return redirect;
                if (hashInfo && hashInfo.params.open_flag === 'crm_detail_page') {
                    return this.handleCrmDetailNewWindowJump(redirect);
                }
                return this.handleEdmPageRouteJump(redirect);
            },
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#edm', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#customer', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#/unitable-crm', {
            handleUrl: (url) => this.handleUnitableCrmRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#wmData', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#systemTask', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#enterpriseSetting', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#site?page=sns', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set(SystemApiImpl.host + '/#sns', {
            handleUrl: (url) => this.handleEdmPageRouteJump(url),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/mail/mailsetting', {
            handleUrl: this.handleSettingWebJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/calendar/home', {
            handleUrl: this.handleScheduleJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/calendar/jump_schedule_detail', {
            handleUrl: this.handleScheduleJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/globalSearch/home', {
            handleUrl: this.handleGlobalSearchJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/customsData/home', {
            handleUrl: this.handleCustomsDataJump.bind(this),
        });
        this.redirectUrlPrefix.set('https://hubble.netease.com/sl/aaag0w', {
            handleUrl: this.handleImWebJump.bind(this),
        });
        const mailHandler = {
            handleUrl: this.handleMailUrl.bind(this),
            needTransferUseApi: false,
            openInElectron: false,
        };
        this.redirectUrlPrefix.set(this.webmailBJHost, mailHandler);
        this.redirectUrlPrefix.set(this.webmailHZHost, mailHandler);
        this.redirectUrlPrefix.set('https://router.lx.netease.com/sirius/desktop/unitableReport', {
            handleUrl: this.handleDesktopUnitableReport.bind(this),
        });
        this.throttlePlaySystemAudio = throttle(this.playSystemAudio, 1500, { trailing: false });
        this.playScheduleNoticeAudio = throttle(this.playScheduleNoticeAudio, 1500, { trailing: false });
    }
    getContextPath() {
        return config('contextPath') || '';
    }
    async getUserConfig(keys) {
        const contextUrl = this.getUrl('getUserConfig');
        const params = { accountAttrs: keys };
        const result = await this.httpApi
            .post(contextUrl, params, { contentType: 'json' })
            .then((res) => {
            var _a;
            if (res && res.data && res.data.success) {
                return (_a = res.data.data) === null || _a === void 0 ? void 0 : _a.accountAttrs;
            }
            return Promise.reject(new Error('请求未成功'));
        })
            .catch(e => {
            console.warn('getUserConfig error', e);
            return [];
        });
        return result;
    }
    async setUserConfig(attrs) {
        const contextUrl = this.getUrl('setUserConfig');
        const params = { accountAttrs: attrs };
        const result = await this.httpApi
            .post(contextUrl, params, { contentType: 'json' })
            .then((res) => !!(res && res.data && res.data.success))
            .catch(e => {
            console.warn('setMailConfig error', e);
            return false;
        });
        return result;
    }
    getTimeZoneList() {
        return [
            { key: 999, value: `${getIn18Text('timeZoneDefault')} (${Intl.DateTimeFormat().resolvedOptions().timeZone})` },
            { key: 0, schedule_zone_id: 157, value: getIn18Text('timeZone0') },
            { key: 1, schedule_zone_id: 173, value: getIn18Text('timeZone1') },
            { key: 2, schedule_zone_id: 192, value: getIn18Text('timeZone2') },
            { key: 3, schedule_zone_id: 225, value: getIn18Text('timeZone3') },
            { key: 4, schedule_zone_id: 232, value: getIn18Text('timeZone4') },
            { key: 5, schedule_zone_id: 245, value: getIn18Text('timeZone5') },
            { key: 6, schedule_zone_id: 262, value: getIn18Text('timeZone6') },
            { key: 7, schedule_zone_id: 271, value: getIn18Text('timeZone7') },
            { key: 8, schedule_zone_id: 290, value: getIn18Text('timeZone8') },
            { key: 9, schedule_zone_id: 305, value: getIn18Text('timeZone9') },
            { key: 10, schedule_zone_id: 310, value: getIn18Text('timeZone10') },
            { key: 11, schedule_zone_id: 331, value: getIn18Text('timeZone11') },
            { key: 12, schedule_zone_id: 333, value: getIn18Text('timeZone12') },
            { key: -1, schedule_zone_id: 146, value: getIn18Text('timeZone-1') },
            { key: -2, schedule_zone_id: 143, value: getIn18Text('timeZone-2') },
            { key: -3, schedule_zone_id: 140, value: getIn18Text('timeZone-3') },
            { key: -4, schedule_zone_id: 85, value: getIn18Text('timeZone-4') },
            { key: -5, schedule_zone_id: 84, value: getIn18Text('timeZone-5') },
            { key: -6, schedule_zone_id: 43, value: getIn18Text('timeZone-6') },
            { key: -7, schedule_zone_id: 26, value: getIn18Text('timeZone-7') },
            { key: -8, schedule_zone_id: 20, value: getIn18Text('timeZone-8') },
            { key: -9, schedule_zone_id: 9, value: getIn18Text('timeZone-9') },
            { key: -10, schedule_zone_id: 4, value: getIn18Text('timeZone-10') },
            { key: -11, schedule_zone_id: 1, value: getIn18Text('timeZone-11') },
            { key: -12, value: getIn18Text('timeZone-12') },
        ];
    }
    getTimeZoneListByLang(lang) {
        return [
            { key: 999, value: `${getIn18TextByLang('timeZoneDefault', lang)} (${Intl.DateTimeFormat().resolvedOptions().timeZone})` },
            { key: 0, schedule_zone_id: 157, value: getIn18TextByLang('timeZone0', lang) },
            { key: 1, schedule_zone_id: 173, value: getIn18TextByLang('timeZone1', lang) },
            { key: 2, schedule_zone_id: 192, value: getIn18TextByLang('timeZone2', lang) },
            { key: 3, schedule_zone_id: 225, value: getIn18TextByLang('timeZone3', lang) },
            { key: 4, schedule_zone_id: 232, value: getIn18TextByLang('timeZone4', lang) },
            { key: 5, schedule_zone_id: 245, value: getIn18TextByLang('timeZone5', lang) },
            { key: 6, schedule_zone_id: 262, value: getIn18TextByLang('timeZone6', lang) },
            { key: 7, schedule_zone_id: 271, value: getIn18TextByLang('timeZone7', lang) },
            { key: 8, schedule_zone_id: 290, value: getIn18TextByLang('timeZone8', lang) },
            { key: 9, schedule_zone_id: 305, value: getIn18TextByLang('timeZone9', lang) },
            { key: 10, schedule_zone_id: 310, value: getIn18TextByLang('timeZone10', lang) },
            { key: 11, schedule_zone_id: 331, value: getIn18TextByLang('timeZone11', lang) },
            { key: 12, schedule_zone_id: 333, value: getIn18TextByLang('timeZone12', lang) },
            { key: -1, schedule_zone_id: 146, value: getIn18TextByLang('timeZone-1', lang) },
            { key: -2, schedule_zone_id: 143, value: getIn18TextByLang('timeZone-2', lang) },
            { key: -3, schedule_zone_id: 140, value: getIn18TextByLang('timeZone-3', lang) },
            { key: -4, schedule_zone_id: 85, value: getIn18TextByLang('timeZone-4', lang) },
            { key: -5, schedule_zone_id: 84, value: getIn18TextByLang('timeZone-5', lang) },
            { key: -6, schedule_zone_id: 43, value: getIn18TextByLang('timeZone-6', lang) },
            { key: -7, schedule_zone_id: 26, value: getIn18TextByLang('timeZone-7', lang) },
            { key: -8, schedule_zone_id: 20, value: getIn18TextByLang('timeZone-8', lang) },
            { key: -9, schedule_zone_id: 9, value: getIn18TextByLang('timeZone-9', lang) },
            { key: -10, schedule_zone_id: 4, value: getIn18TextByLang('timeZone-10', lang) },
            { key: -11, schedule_zone_id: 1, value: getIn18TextByLang('timeZone-11', lang) },
            { key: -12, value: getIn18TextByLang('timeZone-12', lang) },
        ];
    }
    getSystemTimeZone(lang) {
        let useLocalZone = false;
        let timeZone = 8;
        const ckRes = checkMasterInfo();
        const judgeWay = (ckRes === null || ckRes === void 0 ? void 0 : ckRes.isMaster) || process.env.MOCK_ENV === 'mobile' ? 'header' : 'cookie';
        if (judgeWay === 'header') {
            useLocalZone = true;
            timeZone = useLocalZone ? -(new Date().getTimezoneOffset() / 60) : 8;
        }
        else {
            useLocalZone = this.mailConfApi.getLocalTimezone();
            timeZone = useLocalZone ? -(new Date().getTimezoneOffset() / 60) : this.mailConfApi.getTimezone();
        }
        return (lang ? this.getTimeZoneListByLang(lang) : this.getTimeZoneList()).find(v => v.key === timeZone);
    }
    getDateByTimeZone(originDate, timeDiff, isMailDate) {
        try {
            const ckRes = checkMasterInfo();
            const judgeWay = (ckRes === null || ckRes === void 0 ? void 0 : ckRes.isMaster) || process.env.MOCK_ENV === 'mobile' ? 'header' : 'cookie';
            let useLocalTimeZone = false;
            if (judgeWay === 'header') {
                useLocalTimeZone = true;
            }
            else {
                useLocalTimeZone = this.mailConfApi.getLocalTimezone();
            }
            let _timeDiff = useLocalTimeZone ? 8 : timeDiff;
            let _originDate = originDate;
            const offset = -8;
            if (typeof originDate === 'string' && Number.isInteger(Number(originDate))) {
                _originDate = Number(originDate);
            }
            else if (typeof originDate === 'string' && originDate.indexOf('-') !== -1) {
                _originDate = originDate.replace(/-/g, '/');
            }
            else {
                _originDate = originDate;
            }
            _originDate = new Date(_originDate);
            if (useLocalTimeZone && isMailDate) {
                _timeDiff = -(new Date().getTimezoneOffset() / 60);
            }
            return _originDate.getTime() + (_timeDiff + offset) * 60 * 60 * 1000;
        }
        catch (error) {
            console.warn(`getDateByTimeZone error: originDate ${originDate}`);
            return 0;
        }
    }
    timeZoneTrans(timeStr, fromTimeZone, toTimeZone) {
        var _a;
        const dateArr = timeStr === null || timeStr === void 0 ? void 0 : timeStr.trim().split(/\s+/);
        if ((dateArr === null || dateArr === void 0 ? void 0 : dateArr.length) < 2)
            return null;
        let fromTimeZone1 = Number(fromTimeZone);
        if (fromTimeZone1 - 999 === 0) {
            const systemTimeZone = ((_a = this.getSystemTimeZone()) === null || _a === void 0 ? void 0 : _a.key) || 8;
            fromTimeZone1 = systemTimeZone;
        }
        const abs = Math.abs(fromTimeZone1);
        const utcStr = dateArr[0] + 'T' + dateArr[1] + (fromTimeZone1 > 0 ? '+' : '-') + (abs < 10 ? '0' + abs : abs) + ':00';
        const selectedMoment = moment(utcStr);
        const systemMoment = moment(selectedMoment.valueOf()).utcOffset(Number(toTimeZone));
        return systemMoment;
    }
    getSystemProxyTypeSync() {
        if (!process.env.BUILD_ISELECTRON) {
            return defaultSystemProxyType;
        }
        return SystemApiImpl.systemProxyType;
    }
    updateHostType() {
        if (!inWindow())
            return;
        let hostType = '';
        if (process.env.BUILD_ISELECTRON) {
            const hostTypeStoreInfo = this.storeApi.getSync(SystemApiImpl.STORE_HOST_TYPE_KEY, SystemApiImpl.STORE_HOST_TYPE_CONFIG);
            if (hostTypeStoreInfo.suc && hostTypeStoreInfo.data) {
                hostType = hostTypeStoreInfo.data;
            }
        }
        else {
            const hostName = document.location.host;
            const hostNameToHostType = process.env.BUILD_ISEDM
                ? {
                    'waimao-domestic.office.163.com': SystemApiImpl.hostTypes.domestic,
                    'waimao.office.163.com': SystemApiImpl.hostTypes.smartDNSHost,
                }
                : {
                    'lingxi-domestic.office.163.com': SystemApiImpl.hostTypes.domestic,
                    'lingxi.office.163.com': SystemApiImpl.hostTypes.smartDNSHost,
                };
            hostType = hostNameToHostType[hostName];
        }
        if (hostType) {
            if (hostType === SystemApiImpl.hostTypes.domestic) {
                SystemApiImpl.useDomesticHost = true;
                this.setProxyOn(true);
                return;
            }
        }
        this.setProxyOn(false);
        SystemApiImpl.useDomesticHost = false;
    }
    getWebMailLangStr() {
        if (inWindow()) {
            return window.systemLang === 'en' ? 'en_US' : 'zh_CN';
        }
        return 'zh_CN';
    }
    decodeSessionName(_session) {
        if (!_session)
            return null;
        const sessionStr = decodeURIComponent(_session);
        const sessionArr = sessionStr.split(':');
        if (sessionArr[1]) {
            let mainEmail = '';
            if (window && window.isAccountBg) {
                mainEmail = this.getMainAccount().email;
            }
            else {
                const currentUser = this.getCurrentUser();
                mainEmail = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) || '';
            }
            if (mainEmail) {
                const subEmail = sessionArr[1].replace(`${mainEmail}-`, '');
                return {
                    mainEmail,
                    subEmail,
                };
            }
        }
        return null;
    }
    async getActiveUserTrackParams(isShareAccount) {
        try {
            const trackParam = {
                installSource: '',
                language: '',
                agentNumber: 0,
                accountType: '主账号',
                otherApp: '',
            };
            const langMap = {
                zh: '中文版',
                en: '英文版',
                'zh-trad': '繁体中文版',
            };
            trackParam.language = langMap[window.systemLang || DEFAULT_LANG];
            if (process.env.BUILD_ISELECTRON && window.process) {
                const { process } = window;
                const isX64 = process.arch.toLowerCase() === 'x64';
                const { isMac } = window.electronLib.env;
                if (isMac) {
                    trackParam.installSource = isX64 ? 'dmg_x64' : 'dmg_arm64';
                }
                else if (this.isMsiBuild) {
                    trackParam.installSource = isX64 ? 'msi_x64' : 'msi_ia32';
                }
                else {
                    trackParam.installSource = isX64 ? 'exe_x64' : 'exe_ia32';
                }
            }
            if (process.env.BUILD_ISEDM && process.env.BUILD_ISWEB && buildProfile === 'webedm_prod_gray') {
                trackParam.installSource = 'edm_gray';
            }
            const currentUser = this.getCurrentUser();
            const subAccounts = currentUser ? await this.accountApi.getSubAccounts({ expired: false }) : [];
            trackParam.agentNumber = subAccounts && subAccounts.length ? subAccounts.length : 0;
            const hasIsShareAccountParam = typeof isShareAccount !== 'undefined';
            if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.isSharedAccount) {
                trackParam.accountType = '公共账号-切换';
            }
            else {
                const isSharedAccount = hasIsShareAccountParam ? isShareAccount : await this.accountApi.getIsSharedAccountAsync();
                if (isSharedAccount) {
                    trackParam.accountType = '公共账号-授权码登录';
                }
            }
            if (!process.env.BUILD_ISELECTRON && window.isInOtherApp) {
                if (window.isInDingTalk) {
                    trackParam.otherApp = 'DingTalk';
                }
                if (window.isInWxWork) {
                    trackParam.otherApp = 'WxWork';
                }
                if (window.isInFeiShu) {
                    trackParam.otherApp = 'Lark';
                }
            }
            return trackParam;
        }
        catch (ex) {
            console.error(ex);
            return {};
        }
    }
    handleMailReadingPageJump(link, urlHandler) {
        if (urlHandler) {
            console.log('has urlHandler : ', urlHandler);
        }
        const url = new URL(link);
        const qr = new URLSearchParams(url.search);
        const mid = qr.get('mailId') || qr.get('mid');
        if (this.isElectron()) {
            this.createWindowWithInitData({ type: 'readMail', additionalParams: { account: '' } }, {
                eventName: 'initPage',
                eventData: mid,
                _account: '',
            });
        }
        else {
            this.openNewWindow(SystemApiImpl.contextPath + `/readMail/?id=${mid}`);
        }
        return '';
    }
    handleMailReadingOnlyPageJump(link, urlHandler) {
        if (urlHandler) {
            console.log('has urlHandler : ', urlHandler);
        }
        const url = new URL(link);
        const qr = new URLSearchParams(url.search);
        const mid = qr.get('mid') || qr.get('id');
        const teamId = qr.get('teamId');
        if (mid) {
            if (this.isElectron()) {
                this.createWindowWithInitData({ type: 'readMailReadOnly', additionalParams: { account: '' } }, {
                    eventName: 'initPage',
                    eventData: { mid, teamId },
                    eventStrData: '',
                    _account: '',
                }).then();
            }
            else {
                window.open(SystemApiImpl.contextPath + `/readMailReadOnly/?id=${mid}&teamId=${teamId}`, 'readMail', 'menubar=0,scrollbars=1,resizable=1,width=800,height=600');
            }
        }
        return '';
    }
    handleDiskPageRouteJump(link, urlHandler) {
        if (urlHandler) {
            console.log('has urlHandler : ', urlHandler);
        }
        const url = new URL(link);
        const searchUrl = url.hash.split('?')[1];
        if (searchUrl) {
            const searchParams = new URLSearchParams(searchUrl);
            const startTime = searchParams.get('shareTime') || searchParams.get('startTime');
            const endTime = searchParams.get('endTime');
            if (startTime && endTime) {
                this.eventApi.sendSysEvent({
                    eventName: 'routeChange',
                    eventStrData: 'gatsbyStateNav',
                    eventData: {
                        url: '/#disk',
                        state: { defaultRangeTime: [Number(startTime), Number(endTime)] },
                    },
                });
            }
        }
        return '';
    }
    handleEdmPageRouteJump(link) {
        const urlWithoutHost = link.substring(SystemApiImpl.host.length);
        if (this.isElectron()) {
            this.eventApi.sendSysEvent({
                eventName: 'routeChange',
                eventStrData: 'gatsbyStateNav',
                eventData: {
                    url: urlWithoutHost,
                },
            });
            return '';
        }
        return this.handleInternalUrlWebJump(link);
    }
    handleCrmDetailNewWindowJump(link) {
        const hashInfo = getHashInfo(link);
        if (this.isElectron() && hashInfo) {
            this.createWindowWithInitData({
                type: 'unitableCrmDetailPage',
                additionalParams: {},
            }, {
                eventName: 'initPage',
                eventData: Object.assign({ detailUrl: `#${hashInfo.hash}` }, hashInfo.params),
            });
            return '';
        }
        return this.handleInternalUrlWebJump(link);
    }
    handleUnitableCrmRouteJump(link) {
        if (this.isElectron()) {
            const getHashString = (url) => {
                const hashIndex = url.indexOf('#/');
                if (hashIndex === -1) {
                    return '';
                }
                return url.substring(hashIndex);
            };
            window.location.hash = getHashString(link);
            return '';
        }
        return this.handleInternalUrlWebJump(link);
    }
    handleInternalUrlWebJump(url, urlHandler) {
        const parseUrl = new URL(url);
        if (parseUrl && parseUrl.pathname) {
            if (urlHandler && this.isElectron()) {
                if (urlHandler.openInElectron) {
                    if (!urlHandler.openInternalPage) {
                        const newUrl = 'customer';
                        urlHandler.originUrl = url;
                        return newUrl;
                    }
                    const _pathName = parseUrl.pathname.replaceAll('/', '');
                    urlHandler.data = {
                        eventName: 'initPage',
                        eventData: {
                            hash: url,
                        },
                    };
                    if (SystemApiImpl.resourceRouterConfig.indexOf(_pathName) !== -1) {
                        if (url.indexOf('targetWindow') !== -1) {
                            const paramsObj = this.parseUrlObjectParams(parseUrl.hash);
                            urlHandler.data.eventData.windowId = paramsObj.targetWindow;
                            urlHandler.data.eventData.hash = url.replace(/\&targetWindow=\w+/, '');
                        }
                        if (_pathName !== 'share') {
                            const { hash } = urlHandler.data.eventData;
                            if (hash === null || hash === void 0 ? void 0 : hash.includes('type=')) {
                                urlHandler.data.eventData.hash = hash === null || hash === void 0 ? void 0 : hash.replace(/type=\w+/, `type=${_pathName}`);
                            }
                            else {
                                urlHandler.data.eventData.hash = `${hash}&type=${_pathName}`;
                            }
                        }
                        return 'resources';
                    }
                    return _pathName;
                }
                const newUrl = SystemApiImpl.loginPage + '#' + encodeURIComponent(parseUrl.pathname + (parseUrl.hash ? parseUrl.hash : ''));
                return newUrl;
            }
        }
        return url;
    }
    handleDiskShareWebJump(url, urlHandler) {
        var _a;
        const parseUrl = new URL(url);
        if (parseUrl && parseUrl.pathname && urlHandler && this.isElectron() && urlHandler.openInElectron) {
            const _pathName = parseUrl.pathname.replaceAll('/', '');
            const params = this.parseUrlObjectParams((_a = parseUrl.hash) === null || _a === void 0 ? void 0 : _a.toLowerCase());
            if (_pathName === 'share' && (params === null || params === void 0 ? void 0 : params.type) === 'folder') {
                this.netStorageApi
                    .getLinkInfo({
                    linkUrl: url,
                })
                    .then((res) => {
                    var _a, _b;
                    params.from = ((_b = (_a = res.content) === null || _a === void 0 ? void 0 : _a.bizCode) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || params.from;
                    this.eventApi.sendSysEvent({
                        eventName: 'routeChange',
                        eventStrData: 'gatsbyStateNav',
                        asInnerMsg: true,
                        eventData: {
                            url: '/#disk',
                            state: { anchorFolder: params },
                        },
                    });
                });
                return '';
            }
        }
        return this.handleInternalUrlWebJump(url, urlHandler);
    }
    handleSettingWebJump() {
        this.eventApi.sendSysEvent({
            eventName: 'routeChange',
            eventStrData: 'gatsbyNav',
            eventData: {
                url: '/#setting',
                state: {
                    currentTab: 'mail',
                },
            },
        });
        return '';
    }
    handleImWebJump() {
        this.eventApi.sendSysEvent({
            eventName: 'routeChange',
            eventStrData: 'gatsbyNav',
            eventData: {
                url: '/#message',
                state: {
                    currentTab: 'mail',
                },
            },
        });
        return '';
    }
    handleDesktopUnitableReport(link) {
        console.log(link);
        const url = new URL(link);
        const siriusLinkControlFlag = url.searchParams.get('siriusLinkControlFlag');
        const siriusLinkControlPayload = url.searchParams.get('siriusLinkControlPayload');
        let payloadData = {};
        try {
            payloadData = siriusLinkControlPayload ? JSON.parse(decodeURIComponent(siriusLinkControlPayload)) : {};
        }
        catch (error) {
            payloadData = {};
        }
        let target = 'appsHome';
        let dataTrackOperaType = '';
        switch (siriusLinkControlFlag) {
            case 'appDailyReportWrite':
                target = 'appsDailyReport';
                dataTrackOperaType = `daily_${payloadData.reportEventSource}`;
                break;
            case 'appWeeklyReportWrite':
                target = 'appsWeeklyReport';
                dataTrackOperaType = `weekly_${payloadData.reportEventSource}`;
                break;
            case 'appViewMoreReports':
                const isweekly = payloadData.reportType === 'weekly';
                target = isweekly ? 'appsWeeklyReportDetail' : 'appsDailyReportDetail';
                dataTrackOperaType = `${payloadData.reportType}_view`;
                break;
            default:
                break;
        }
        this.dataTracker.track('report_mail_behavior', {
            opera_type: dataTrackOperaType,
        });
        this.eventApi.sendSysEvent({
            eventName: 'routeChange',
            eventStrData: 'gatsbyStateNav',
            eventData: {
                url: `#apps?pageId=${target}`,
                state: {
                    siriusLinkControlPayload,
                },
            },
        });
        return '';
    }
    handleScheduleJump(link, urlHandler) {
        if (urlHandler) {
            console.log('has urlHandler : ', urlHandler);
        }
        const url = new URL(link);
        if (url.search) {
            const searchParams = new URLSearchParams(url.search);
            const time = searchParams.get('time') || +new Date();
            this.eventApi.sendSysEvent({
                eventName: 'routeChange',
                eventStrData: 'gatsbyStateNav',
                eventData: {
                    url: '/#schedule',
                    state: { type: 'timeGridWeek', time },
                },
            });
        }
        return '';
    }
    handleGlobalSearchJump(link, urlHandler) {
        if (urlHandler) {
            console.log('has urlHandler : ', urlHandler);
        }
        const url = new URL(link);
        const searchParams = new URLSearchParams(url.search);
        const page = searchParams.get('page') || '';
        this.eventApi.sendSysEvent({
            eventName: 'routeChange',
            eventStrData: 'gatsbyStateNav',
            eventData: {
                url: `/#wmData?page=${page}`,
            },
        });
        return '';
    }
    handleCustomsDataJump(link) {
        const url = new URL(link);
        const searchParams = new URLSearchParams(url.search);
        const page = searchParams.get('page') || '';
        this.eventApi.sendSysEvent({
            eventName: 'routeChange',
            eventStrData: 'gatsbyStateNav',
            eventData: {
                url: `/#wmData?page=${page}`,
            },
        });
        return '';
    }
    handleMailUrl(url, urlConfig) {
        console.log('[sys] handle mail Url', url, urlConfig);
        const currentUser = this.getCurrentUser();
        if (currentUser) {
            const searchValue = /sid=[0-9A-Za-z*\-_]+/i;
            if (searchValue.test(url)) {
                return url.replace(searchValue, 'sid=' + currentUser.sessionId);
            }
            return this.httpApi.buildUrl(url, { sid: currentUser.sessionId });
        }
        return url;
    }
    async handleJumpUrl(winid, jumpUrl, customerHandler) {
        if (typeof winid === 'string' && !jumpUrl) {
            jumpUrl = String(winid);
            winid = undefined;
        }
        if (systemApi.isWebWmEntry() && jumpUrl.split('.com')[1]) {
            const forWmJumpUrl = ruleEngine(jumpUrl.split('.com')[1], null);
            forWmJumpUrl && (jumpUrl = `${location.origin}/${forWmJumpUrl}`);
            console.log('[sys] open window from web-entry-wm: ', jumpUrl);
        }
        console.log('[sys] open window from win:' + winid, jumpUrl);
        if (jumpUrl && typeof jumpUrl === 'string') {
            if (jumpUrl.startsWith('/systemTask')) {
                if (!this.inEdm()) {
                    return this.eventApi.sendSysEvent({
                        auto: true,
                        eventSeq: 0,
                        eventName: 'error',
                        eventLevel: 'error',
                        eventData: {
                            title: '请在网易外贸通中打开链接',
                            popupType: 'toast',
                            popupLevel: 'info',
                        },
                    });
                }
                const params = new URLSearchParams(jumpUrl.split('?')[1]);
                return this.taskCenterApi.handleSystemTask({
                    moduleType: params.get('moduleType'),
                    taskType: params.get('taskType'),
                    bizId: params.get('bizId'),
                    bizContent: encodeURIComponent(params.get('bizContent')),
                });
            }
            let urlHandler;
            try {
                const { url, succ, h } = await this.buildJumpUrl(jumpUrl, customerHandler);
                urlHandler = h;
                if (!succ) {
                }
                if (urlHandler) {
                    if (!urlHandler.openInternalPage && this.isElectron() && window.electronLib && urlHandler.openInElectron) {
                        this.createWindow({ type: url, url: urlHandler.originUrl, setMainWindowCookie: true }).then();
                    }
                    else if (urlHandler.openInternalPage && this.isElectron() && window.electronLib && urlHandler.openInElectron && urlHandler.data) {
                        this.createWindowWithInitData(url, urlHandler.data).then();
                    }
                    else if (url && url.length > 0) {
                        this.openNewWindow(url, urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.openInElectron);
                    }
                    return;
                }
            }
            catch (reason) {
                console.warn('[sys] handle url error:' + jumpUrl, reason);
            }
            this.openNewWindow(jumpUrl);
        }
    }
    inEdm() {
        return process.env.BUILD_ISEDM;
    }
    inElectronBuild() {
        return process.env.BUILD_ISELECTRON;
    }
    inWebBuild() {
        return process.env.BUILD_ISWEB;
    }
    inLingXiBuild() {
        return process.env.BUILD_ISLINGXI;
    }
    async buildJumpUrl(url, customerHandler) {
        var _a;
        let urlHandler = customerHandler;
        let matchedPattern;
        let succ = false;
        if (!urlHandler) {
            if (isInredirectWhiteList(url)) {
                urlHandler = {
                    needTransferUseApi: false,
                    needUploadAccountInfo: false,
                };
            }
        }
        if (!urlHandler) {
            this.redirectUrlPrefix.forEach((value, key) => {
                if (url.startsWith(key)) {
                    if (!matchedPattern || matchedPattern.length < key.length) {
                        urlHandler = value;
                        matchedPattern = key;
                    }
                }
            });
        }
        this.loggerApi.track('jumpWithLogin', {
            url,
            urlTrans: !!(urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.handleUrl),
            openInWin: !!(urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.openInElectron),
            needTransfer: urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.needTransferUseApi,
            needUploadAccount: urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.needUploadAccountInfo,
            openInternal: urlHandler === null || urlHandler === void 0 ? void 0 : urlHandler.openInternalPage,
        });
        if (urlHandler && urlHandler.handleUrl) {
            url = urlHandler.handleUrl(url, urlHandler);
        }
        const newUKey = urlHandler && urlHandler.needUploadAccountInfo ? await this.uploadAccountInfo() : '';
        if (urlHandler && urlHandler.needTransferUseApi) {
            const baseUrl = this.getUrl('getUrlWithTicket');
            const res = await this.httpApi.post(baseUrl, {
                userInfo: encodeURIComponent(newUKey),
                redirectUrl: encodeURIComponent(url),
            });
            if (res && res.data && res.data.success) {
                const ticket = (_a = res.data.data) === null || _a === void 0 ? void 0 : _a.ticket;
                if (ticket) {
                    const newUrl = this.httpApi.buildUrl(this.getUrl('jumpUrl'), {
                        ticket,
                    });
                    this.loggerApi.track('jumpWithLoginSuccess', { url, jump: newUrl });
                    url = newUrl;
                    succ = true;
                }
            }
        }
        else {
            succ = true;
        }
        return { url, succ, h: urlHandler };
    }
    getNativeClipBoard() {
        if (inWindow() && this.isElectron() && window.electronLib) {
            return window.electronLib.appManage.getClipBoard();
        }
        return undefined;
    }
    getScreenCapture(data = {}) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            return window.electronLib.appManage.screenCapture(JSON.stringify(data));
        }
        return undefined;
    }
    setScreenCaptureAccess() {
        if (inWindow() && this.isElectron() && window.electronLib) {
            return window.electronLib.appManage.toggleCaptureScreenAccess();
        }
        return undefined;
    }
    setScreenCaptureShortcut(data = {}) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            return window.electronLib.appManage.screenCaptureShortcut(JSON.stringify(data));
        }
        return undefined;
    }
    setMinimizeGlobalShortcutUI(data = {}) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            return window.electronLib.appManage.setMinimizeGlobalShortcut(JSON.stringify(data));
        }
        return undefined;
    }
    async uploadAccountInfo() {
        const newUKey = this.generateKey(16);
        const setUrl = this.getUrl('setData');
        const { localList: re, current } = await this.accountApi.doGetAccountList(true);
        if (current)
            re.push(current);
        if (re && re.length > 0) {
            const udata = this.encryptByKey(JSON.stringify(re[0]), newUKey);
            this.httpApi.post(setUrl, { info: udata }).then(res => {
                console.log('[sys] upload account info got:', res);
            });
        }
        return newUKey;
    }
    async getLocalLoginToken(account, pass) {
        const newUKey = this.generateKey(16);
        const storedAccount = {
            account_name: account,
            password: pass,
            lastLogin: Date.now(),
        };
        const udata = this.encryptByKey(JSON.stringify(storedAccount), newUKey);
        await this.storeApi.put(newUKey, udata, {
            prefix: 'lg-',
            noneUserRelated: true,
        });
        return newUKey;
    }
    getCurrentWinInfo(noStatus) {
        if (inWindow() && window.electronLib) {
            if (noStatus && this.winInfo) {
                return Promise.resolve(this.winInfo);
            }
            return window.electronLib.windowManage.getCurWindow().then((ev) => {
                if (ev) {
                    this.winInfo = ev;
                    return ev;
                }
                return Promise.reject(new Error('无法找到当前窗口的信息，这个错误不应该发生'));
            });
        }
        throw new Error('当前环境不支持该调用');
    }
    getCurrentModule() {
        if (this.isMainPage()) {
            const { hash } = window.location;
            return hash.split('?')[0].slice(1);
        }
        return 'other';
    }
    getCachedCurrentWinInfo() {
        return this.winInfo;
    }
    setUserProp(key, value, store) {
        this.storeApi.setUserProp(key, value, store);
    }
    get notificationAvailableLabel() {
        return this._notificationAvailableLabel;
    }
    get currentShowedNotification() {
        return this._currentShowedNotification;
    }
    watchEventOnOffLine(evt) {
        console.log('[sys] system on off line:', evt);
        if (evt.type === 'offline') {
            SystemApiImpl.networkFail = 1000;
        }
        else if (evt.type === 'online') {
            SystemApiImpl.networkFail -= 1000;
            if (SystemApiImpl.networkFail < 0) {
                SystemApiImpl.networkFail = 0;
            }
        }
    }
    async getAllWindow(type) {
        if (this.isElectron() && window.electronLib) {
            let winInfos = await window.electronLib.windowManage.getAllWinInfo();
            if (type) {
                winInfos = winInfos.filter(it => it.type === type);
            }
            return winInfos;
        }
        return [];
    }
    watchNetworkFail(ev) {
        if (ev.eventStrData === 'warn') {
            SystemApiImpl.networkFail += 1;
            if (SystemApiImpl.networkFail > 10) {
                SystemApiImpl.networkFail = 10;
            }
        }
        else if (ev.eventStrData === 'suc') {
            SystemApiImpl.networkFail -= 2;
            if (SystemApiImpl.networkFail < 0) {
                SystemApiImpl.networkFail = 0;
            }
        }
    }
    watchLogin(ev) {
        if (ev && ev.eventData) {
        }
        else {
        }
    }
    beforeLogout(ev) {
        if (this.isElectron() && inWindow() && window.electronLib && this.winMap) {
            console.log('[sys] clear all window and close all window', this.winMap, ev);
            if (this.winMap) {
                Object.keys(this.winMap).forEach(i => {
                    if (i && Object.prototype.hasOwnProperty.apply(this.winMap, [i])) {
                        !!this.winMap && delete this.winMap[Number(i)];
                    }
                });
            }
            window.electronLib.windowManage.closeAllWindowExceptMain(true).then();
        }
        return this.name;
    }
    createWindow(type, data) {
        if (inWindow() && this.isElectron() && window.electronLib && type) {
            const { windowManage } = window.electronLib;
            const config = typeof type === 'string'
                ? {
                    type,
                }
                : type;
            return windowManage.createWindow(config).then((res) => {
                console.log('[sys] create new window return:', res, config, type);
                if (res.winId &&
                    !config.manualShow &&
                    config.type !== 'scheduleReminder' &&
                    config.type !== 'downloadReminder' &&
                    config.type !== 'advertisingReminder') {
                    window.electronLib.windowManage.show(res.winId);
                }
                this.saveWinInfo(res, type, data);
                return res;
            });
        }
        return Promise.reject(new Error('not supported or param error'));
    }
    saveWinInfo(res, type, data) {
        const tp = typeof type === 'object' ? type.type : type;
        if (!this.winMap) {
            this.winMap = {};
        }
        if (res.winId) {
            this.winMap[res.winId] = {
                win: res,
                type: tp,
                initData: data,
            };
        }
    }
    async filterDuplicateWindow(data) {
        if (data.uniqueId && this.uniqueWinMap.has(data.uniqueId)) {
            const winId = this.uniqueWinMap.get(data.uniqueId);
            const r = winId && this.winMap ? this.winMap[winId] : null;
            if (winId && (r === null || r === void 0 ? void 0 : r.win.success) && (await window.electronLib.windowManage.show(winId))) {
                return r.win;
            }
        }
        return null;
    }
    saveUniqueWindowMap(uniqueId, winId) {
        if (uniqueId && winId) {
            this.uniqueWinMap.set(uniqueId, winId);
            return true;
        }
        return false;
    }
    async getTabEventTarget(data) {
        const { windowId } = data.eventData;
        let eventTarget = '';
        let availableTab;
        if (windowId) {
            eventTarget = windowId === 'new' ? '' : windowId;
            return eventTarget;
        }
        const allWins = await this.getAllWindow();
        availableTab = allWins.find(item => item.type === 'resources' && item.isVisible);
        if (!availableTab) {
            availableTab = allWins.find(item => item.type === 'resources' && !item.isVisible);
        }
        if (availableTab) {
            eventTarget = String(availableTab.webId);
            await window.electronLib.windowManage.show(availableTab.id);
        }
        return eventTarget;
    }
    async createWindowWithInitData(type, data) {
        console.log('[sys] create window type:' + type + ' with data:', data);
        const res = await this.filterDuplicateWindow(data);
        if (res) {
            if (type === 'imgPreviewPage' || type === 'scheduleOpPage') {
                data.eventTarget = String(res.webId);
                this.eventApi.sendSysEvent(data);
            }
            return res;
        }
        if (['advertisingReminder', 'scheduleReminder', 'downloadReminder'].includes(type)) {
            const allWins = await this.getAllWindow();
            const availableWin = allWins.find(item => item.type === type);
            if (availableWin) {
                this.eventApi.sendSysEvent(Object.assign(Object.assign({}, data), { eventTarget: String(availableWin.webId) }));
                return { success: true };
            }
        }
        if (type === 'resources') {
            const eventTarget = await this.getTabEventTarget(data);
            if (eventTarget) {
                data.eventTarget = eventTarget;
                this.eventApi.sendSysEvent(data);
                return { success: true };
            }
        }
        return this.createWindow(type).then((res1) => {
            data.eventTarget = String(res1.webId);
            this.eventApi.sendSysEvent(data);
            this.saveUniqueWindowMap(data.uniqueId, res1.winId);
            return res1;
        });
    }
    closeSubWindow(winId, needIntercept, forceClose) {
        this.loggerApi.track('close_sub_window', {
            href: window.location.href,
            winId,
            forceClose,
            needIntercept,
        });
        if (inWindow() && this.isElectron() && window.electronLib) {
            window.electronLib.windowManage.close({
                winId,
                force: !needIntercept,
                quit: forceClose,
            });
        }
    }
    closeWindow(needIntercept, forceClose) {
        try {
            this.loggerApi.track('close_window', {
                href: window.location.href,
                forceClose,
                needIntercept,
            });
            this.loggerApi.flush();
        }
        catch (e) {
            console.warn('[sys]close window error', e);
        }
        if (inWindow() && this.isElectron() && window.electronLib) {
            window.electronLib.windowManage.close({
                force: !needIntercept,
                quit: forceClose,
            });
        }
    }
    hideWindow(needIntercept) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            window.electronLib.windowManage.hideForClose({ force: !needIntercept });
        }
    }
    async showWin(winid) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            await window.electronLib.windowManage.show(winid);
        }
    }
    async hideWin(winid) {
        if (inWindow() && this.isElectron() && window.electronLib) {
            await window.electronLib.windowManage.hide(winid);
        }
    }
    async prepareWindow(type) {
        const isLowMemoryMode = await this.getIsLowMemoryModeSync();
        const isAppLocking = this.getIsLockApp();
        if (isLowMemoryMode || isAppLocking) {
            return Promise.resolve();
        }
        if (inWindow() && this.isElectron() && window.electronLib) {
            await window.electronLib.windowManage.prepareWindow(type);
        }
    }
    watchPreLogin(ev) {
        if (ev && ev.eventData) {
        }
        else {
        }
    }
    isSysNotificationAvailable() {
        return inWindow() && window.Notification ? Notification.permission : 'unavailable';
    }
    requestSysNotificationPerm() {
        if (!window.Notification) {
            return Promise.resolve(false);
        }
        return Notification.requestPermission()
            .then(res => res === 'granted')
            .catch(() => false);
    }
    reLaunchApp() {
        if (window.electronLib) {
            window.electronLib.appManage.reLaunchApp();
        }
    }
    reloadToMainPage() {
        if (!inWindow())
            return;
        const mainPageUrl = `${location.protocol}//${location.host}${location.pathname}`;
        location.replace(mainPageUrl);
    }
    playSystemAudio() {
        try {
            const res = this.storeApi.getSync('win7Beep');
            const { suc, data } = res;
            if (suc && data === 'false')
                return;
            this._playNoticeAudio();
        }
        catch (error) {
            console.log('[sys] play system audio error:', error);
        }
    }
    playScheduleNoticeAudio() {
        try {
            const res = this.storeApi.getSync('scheduleAudioAvailable');
            const { suc, data } = res;
            if (suc && data === 'false')
                return;
            this._playNoticeAudio();
        }
        catch (error) {
            console.log('[sys] playScheduleNoticeAudio error:', error);
        }
    }
    _playNoticeAudio() {
        try {
            this.systemMusic = new Audio(`${this.contextPath}/ding.mp3`);
            this.systemMusic.play();
        }
        catch (error) {
            console.error('[sys] play system audio error:', error);
        }
    }
    setShouldUseNotification(val) {
        if (process.env.BUILD_ISWEB || process.env.BUILD_ISLINGXI) {
            return;
        }
        try {
            this.storeApi.putSync(WIN7_USE_NOTIFICATION, val ? 'true' : 'false', globalStoreConfig);
        }
        catch (ex) {
            console.error('setShouldUseNotification', ex);
        }
    }
    getShouldUseNotification() {
        const default_value = true;
        if (process.env.BUILD_ISWEB || process.env.BUILD_ISLINGXI) {
            return true;
        }
        try {
            const data = this.storeApi.getSync(WIN7_USE_NOTIFICATION, globalStoreConfig);
            if (data && data.data && data.suc) {
                return data.data === 'true';
            }
            return default_value;
        }
        catch (ex) {
            console.error(ex);
            return default_value;
        }
    }
    playSystemNotificationAudio() {
        try {
            this.throttlePlaySystemAudio && this.throttlePlaySystemAudio();
        }
        catch (error) {
            console.log('[sys] throttle play system audio error:', error);
        }
    }
    showSysNotification(info) {
        if (isWindows7()) {
            const shouldUseNotication = this.getShouldUseNotification();
            if (!shouldUseNotication) {
                this.playSystemNotificationAudio();
                return Promise.resolve(false);
            }
        }
        const st = this.isSysNotificationAvailable();
        if (st === 'unavailable') {
            return Promise.resolve(false);
        }
        if (st === 'granted') {
            if (isWindows7()) {
                this.playSystemNotificationAudio();
            }
            this.createNotification(info);
            return Promise.resolve(true);
        }
        return this.requestSysNotificationPerm().then(res => {
            if (res) {
                return this.showSysNotification(info);
            }
            return false;
        });
    }
    createNotification(info) {
        const isLocking = this.getIsLockApp();
        if (isLocking)
            return;
        const s = info.tag || SystemApiImpl.defaultTag;
        const tag = SystemApiImpl.sysNotificationTags[s];
        const notification = new Notification(info.title, {
            tag: tag + new Date().getTime() / 60000,
            icon: info.icon,
            body: info.content,
        });
        notification.onshow = (ev) => {
            console.log('[sys] notification showed,', ev);
        };
        notification.onerror = (ev) => {
            console.log('[sys] notification error:', ev);
        };
        if (info.confirmCallback) {
            notification.onclick = info.confirmCallback;
        }
        else {
            notification.onclick = ev => {
                console.log('[sys] notification clicked:', ev);
                window.focus();
                notification.close();
            };
        }
    }
    updateAppNotification(conts) {
        const inNative = this.isElectron();
        if (!inNative) {
            return;
        }
        const content = conts;
        if (!Array.isArray(conts.type)) {
            conts.type = [conts.type];
        }
        const types = conts.type;
        types.forEach(type => {
            var _a, _b, _c, _d;
            if (type === 'macDocker' && typeof ((_a = content.content) === null || _a === void 0 ? void 0 : _a.num) !== 'undefined') {
                if (this.isElectron()) {
                    window.electronLib.appManage.setBadgeCount(content.content.num);
                }
            }
            else if (type === 'windowsFlush' && typeof ((_b = content.content) === null || _b === void 0 ? void 0 : _b.num) !== 'undefined') {
                if (this.isElectron() && !this.isMac) {
                    window.electronLib.windowManage.isFocused().then((bool) => {
                        !bool && window.electronLib.windowManage.flashFrame();
                    });
                }
            }
            else if (type === 'macTray' && ((_c = content.content) === null || _c === void 0 ? void 0 : _c.title)) {
                if (this.isElectron() && this.isMac) {
                    window.electronLib.appManage.setTrayTitle(content.content.title);
                }
            }
            else if (type === 'browserTitle' && ((_d = content.content) === null || _d === void 0 ? void 0 : _d.title)) {
                if (typeof window.document !== 'undefined') {
                    document.title = content.content.title;
                }
            }
        });
    }
    getIsWebCustomHost() {
        if (process.env.BUILD_ISELECTRON) {
            return false;
        }
        if (process.env.BUILD_ISWEB) {
            const hostName = location.hostname || location.host;
            if (hostName.includes('cowork.netease'))
                return false;
            const isCustomHost = !hostName.includes('.163.com');
            return isCustomHost;
        }
        return false;
    }
    getCurrentNode(email) {
        return this.storeApi.getCurrentNode(email);
    }
    getCurrentUser(email) {
        return this.storeApi.getCurrentUser(email);
    }
    switchCurrentUser(email) {
        return this.storeApi.switchCurrentUser(email);
    }
    getUsers() {
        return this.storeApi.getUsers();
    }
    getCurrentUserAccountAlias(email) {
        const currentUser = this.getCurrentUser(email);
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.prop) {
            if (Array.isArray(currentUser.prop.accountAlias)) {
                return currentUser.prop.accountAlias;
            }
        }
        return [];
    }
    getIsDomesticHostType() {
        return SystemApiImpl.useDomesticHost;
    }
    getCurrentHostType() {
        return SystemApiImpl.useDomesticHost ? SystemApiImpl.hostTypes.domestic : SystemApiImpl.hostTypes.smartDNSHost;
    }
    jumpToWebHostLogin() {
        if (window && !window.electronLib) {
            const currentHostType = this.getCurrentHostType();
            const hostUrl = process.env.BUILD_ISEDM ? SystemApiImpl.emdHostTypeToUrlMap[currentHostType] : SystemApiImpl.hostTypeToUrlMap[currentHostType];
            if (hostUrl) {
                window.location.href = hostUrl;
            }
        }
    }
    setCurrentHostType(hostType) {
        if (!inWindow())
            return;
        const { hostTypes } = SystemApiImpl;
        if (hostTypes[hostType]) {
            if (hostType === SystemApiImpl.hostTypes.domestic) {
                SystemApiImpl.useDomesticHost = true;
                this.setProxyOn(true);
            }
            else {
                SystemApiImpl.useDomesticHost = false;
                this.setProxyOn(false);
            }
            this.storeApi.putSync(SystemApiImpl.STORE_HOST_TYPE_KEY, hostType, SystemApiImpl.STORE_HOST_TYPE_CONFIG);
        }
        else {
            console.error(`setCurrentHost error, ${hostType} not supported.`);
        }
    }
    getCurrentUserMailMode(_account) {
        var _a;
        const currentUser = this.getCurrentUser(_account);
        const userMailMode = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.prop) === null || _a === void 0 ? void 0 : _a.mailMode;
        return (userMailMode || config('default_mail_mode'));
    }
    getIsCorpMailMode(_account) {
        const mailMode = this.getCurrentUserMailMode(_account);
        return mailMode === SystemApiImpl.corpConf;
    }
    getAccountUrl(url, account) {
        return this.getUrl(url, undefined, undefined, account);
    }
    getUrl(url, currentNode, splitReq, _account) {
        let resultUrl = '';
        let nodeAccount = _account;
        if (!currentNode) {
            const currentUser = this.getCurrentUser();
            if (currentUser) {
                if (_account === currentUser.id || (currentUser.loginAccount && currentUser.loginAccount === _account)) {
                    nodeAccount = '';
                }
            }
        }
        const curNode = currentNode || this.getCurrentNode(nodeAccount);
        if (splitReq) {
            resultUrl = getUrlPre(url, curNode);
        }
        else {
            resultUrl = getUrl(url, curNode);
        }
        if (SystemApiImpl.domesticHost && SystemApiImpl.useDomesticHost) {
            const hostArr = [SystemApiImpl.host, SystemApiImpl.webMailBJHost, SystemApiImpl.webMailHZHost];
            for (let i = 0; i < hostArr.length; ++i) {
                const currHost = hostArr[i];
                if (resultUrl.startsWith(currHost)) {
                    resultUrl = resultUrl.replace(currHost, SystemApiImpl.domesticHost);
                    break;
                }
            }
        }
        return resultUrl;
    }
    handleAccountAndDomain(account) {
        const exec = SystemApiImpl.accountPattern.exec(account);
        if (exec && exec.length === 3) {
            return { account: exec[1], domain: exec[2] };
        }
        return {};
    }
    isElectron() {
        return isElectron();
    }
    isInMobile() {
        return isInMobile();
    }
    async addWindowHooks(type, callback, targetWinId, intercept) {
        if (this.isElectron() && window.electronLib && SystemApiImpl.eventTrans.has(type)) {
            const conf = {
                hooksName: type,
                intercept,
                targetWinId,
            };
            await this.addWindowHookConf(conf);
            const evName = SystemApiImpl.eventTrans.get(type);
            if (evName) {
                return this.eventApi.registerSysEventObserver(evName, {
                    func: ev => {
                        if (ev && ev.eventData) {
                            const { winId, data, extData } = ev.eventData;
                            callback && callback(winId, data, extData);
                        }
                    },
                });
            }
            return Promise.resolve(0);
        }
        return Promise.reject(new Error('环境不支持此方法'));
    }
    addWindowHookConf(conf) {
        if (this.isElectron() && window.electronLib) {
            const cs = Array.isArray(conf) ? conf : [conf];
            return window.electronLib.windowManage.setHooksConfig(cs);
        }
        return Promise.reject(new Error('环境不支持此方法'));
    }
    isMainWindow() {
        return this.isElectron() && this.isMainPage();
    }
    showMainWindow() {
        if (inWindow() && this.isElectron()) {
            try {
                window.electronLib.windowManage.show();
            }
            catch (error) {
                console.warn(error);
            }
        }
    }
    isStartWindow() {
        return this.isElectron() && window.location.pathname.indexOf('/launch') >= 0;
    }
    isTransferringData() {
        return this.isElectron() && window.location.href.indexOf('init=true') > 0 && (window.location.pathname === '/' || window.location.pathname === '/index.html');
    }
    isBkLoginInit() {
        return this.isElectron() && window.location.href.indexOf('bkLoginInit=true') > 0 && window.location.pathname.includes('login');
    }
    isBkStableWindow() {
        return this.isElectron() && locationHelper.isBkPage();
    }
    getIsAddAccountPage() {
        var _a, _b;
        return window.location.href.includes('add-account-page') || ((_b = (_a = window.history) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b.isAddAccountPage);
    }
    getIsAddSubAccountPage() {
        return window && window.location.href.includes('add-sub-account');
    }
    getIsSubAccountInitPage() {
        return window && window.location.href.includes('init-sub-account');
    }
    getIsSubAccountPage() {
        return window && window.location.href.includes('init-sub-account');
    }
    getMainAccount(email) {
        var _a;
        return {
            email: ((_a = this.getCurrentUser(email)) === null || _a === void 0 ? void 0 : _a.id) || '',
        };
    }
    getMainAccount1() {
        var _a;
        if (window && window.isAccountBg) {
            return {
                email: this.getUrlSearchValue('main-account') || '',
            };
        }
        return {
            email: ((_a = this.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) || '',
        };
    }
    getCurrentSubAccount() {
        if (window && window.isAccountBg) {
            return {
                email: this.getUrlSearchValue('sub-account') || '',
            };
        }
        return {
            email: '',
        };
    }
    getCurrentAgentAccount() {
        if (window && window.isAccountBg) {
            return {
                email: this.getUrlSearchValue('agent-account') || '',
            };
        }
        return {
            email: '',
        };
    }
    getIsMainAccount(email) {
        if (!email) {
            return false;
        }
        const mainAccount = this.getCurrentUser();
        const currentAccount = this.getCurrentUser(email);
        return !!(mainAccount && (mainAccount === null || mainAccount === void 0 ? void 0 : mainAccount.id) === (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.id));
    }
    getIsAddPersonalSubAccountPage() {
        return this.isElectron() && window && window.location.href.includes('add-personal-sub-account');
    }
    isMainPage() {
        return locationHelper.isMainPage();
    }
    isWebWmEntry() {
        return !!process.env.IS_WM_ENTRY;
    }
    isWebFfEntry() {
        return !!process.env.IS_FF_ENTRY;
    }
    isInWebWorker() {
        return 'WorkerGlobalScope' in self && typeof self.document === 'undefined';
    }
    openNewWindow(url, openInElectron, callbacks, initData, haveJquery, sessionName, setMainWindowCookie, bounds) {
        if (inWindow()) {
            if (this.isElectron() && window.electronLib) {
                if (openInElectron) {
                    if (!initData) {
                        const createParams = {
                            type: 'customer',
                            url,
                            setMainWindowCookie: typeof setMainWindowCookie === 'boolean' ? setMainWindowCookie : true,
                            hooks: callbacks,
                            haveJquery,
                        };
                        if (bounds) {
                            createParams.bounds = bounds;
                        }
                        if (sessionName)
                            createParams.sessionName = sessionName;
                        this.createWindow(createParams).then();
                    }
                    else {
                        this.createWindowWithInitData({
                            type: 'customer',
                            url,
                            setMainWindowCookie: true,
                            hooks: callbacks,
                            haveJquery,
                        }, initData);
                    }
                }
                else {
                    window.electronLib.windowManage.openWindow(url).then().catch(console.error);
                }
            }
            else {
                console.warn('tag:' + this.name, 'oepn url', url);
                const open = window.open(url, '_blank', 'menubar=yes,toolbar=yes,location=yes,status=yes,resizable=yes,scrollbars=yes');
                if (open) {
                    console.log('[sys] open window ' + url, open);
                }
                else {
                    return 'fail';
                }
            }
        }
        return '';
    }
    webDownloadLink(url, fileName) {
        if (inWindow()) {
            try {
                const downlondA = document.createElement('A');
                downlondA.href = url;
                downlondA.download = fileName || url;
                document.body.appendChild(downlondA);
                downlondA.click();
                document.body.removeChild(downlondA);
            }
            catch (error) {
                console.error('[sys] 下载失败', error);
            }
        }
    }
    getAutoLaunch() {
        var _a, _b;
        if (this.isElectron() && window.electronLib) {
            return (_b = (_a = window === null || window === void 0 ? void 0 : window.electronLib) === null || _a === void 0 ? void 0 : _a.appManage) === null || _b === void 0 ? void 0 : _b.isAppAutoLaunch();
        }
        return Promise.resolve(false);
    }
    setAutoLaunch(val) {
        var _a, _b;
        if (this.isElectron() && window.electronLib) {
            return (_b = (_a = window === null || window === void 0 ? void 0 : window.electronLib) === null || _a === void 0 ? void 0 : _a.appManage) === null || _b === void 0 ? void 0 : _b.setAppAutoLaunch(!!val);
        }
        throw new Error('此环境不支持该调用');
    }
    getProxyOn(reload) {
        if (reload) {
            const data = this.storeApi.getSync(SystemApiImpl.keyOfProxyOn);
            SystemApiImpl.proxyOn = data && data.suc && data.data === 'true';
        }
        return SystemApiImpl.proxyOn;
    }
    async setProxyOn(on) {
        try {
            const ret = await this.storeApi.put(SystemApiImpl.keyOfProxyOn, String(on));
            SystemApiImpl.proxyOn = on;
            return ret === '';
        }
        catch (e) {
            console.warn(e);
            return false;
        }
    }
    decryptMsg(content) {
        return this.getKey()
            .then((key) => this.decryptByKey(content, key))
            .catch(ex => {
            console.warn('[sys] decrypt message error:', ex);
            return '';
        });
    }
    decryptByKey(content, key, useFormat = true) {
        const array = AES.decrypt(content, key, {
            format: useFormat ? JsonFormatter : undefined,
        });
        return array.toString(enc.Utf8);
    }
    encryptMsg(content) {
        return this.getKey()
            .then((key) => this.encryptByKey(content, key))
            .catch(ex => {
            console.warn('encrypt message error:', ex);
            return '';
        });
    }
    encryptByECB(content, keyStr) {
        const key = enc.Utf8.parse(keyStr);
        const src = enc.Utf8.parse(content);
        const encrypted = AES.encrypt(src, key, {
            mode: mode.ECB,
            padding: pad.Pkcs7,
        });
        return encrypted.toString();
    }
    decryptByECB(encrypted, keyStr) {
        const key = enc.Utf8.parse(keyStr);
        const decrypt = AES.decrypt(encrypted, key, {
            mode: mode.ECB,
            padding: pad.Pkcs7,
        });
        return enc.Utf8.stringify(decrypt).toString();
    }
    encryptByKey(content, key, useFormat = true) {
        const encrypt = AES.encrypt(content, key, {
            format: useFormat ? JsonFormatter : undefined,
        });
        return encrypt.toString();
    }
    sha1(content, isBase64Type = false) {
        const wordArr = SHA1(content);
        if (!isBase64Type) {
            return wordArr.toString();
        }
        return wordArr.toString(enc.Base64);
    }
    md5(content, short) {
        if (!short) {
            return MD5(content).toString();
        }
        return MD5(content).toString(enc.Base64);
    }
    intervalEvent(res) {
        if (typeof window === 'undefined' || typeof window.setInterval === 'undefined') {
            return undefined;
        }
        if (res) {
            const element = SystemApiImpl.eventStore[res.eventPeriod];
            if (res.id && element.eventKeys[res.id]) {
                return undefined;
            }
            const _id = element.events.addOb(res);
            res.seq = 0;
            res._id = _id;
            res.id = res.id || '__k-' + _id;
            element.eventKeys[res.id] = res._id;
            if (element.eid < 0) {
                const func = this.buildExecutor(element);
                func();
            }
            else {
            }
        }
        return res._id;
    }
    buildExecutor(element) {
        const handler = () => {
            if (!element.disable) {
                element.events
                    .iterate(ob => {
                    if (!!ob.noUserAuth || (this.getCurrentUser() && !this.storeApi.isLogout())) {
                        ob.seq += 1;
                        if (ob.seq > 10000000) {
                            ob.seq = 0;
                        }
                        setTimeout(() => {
                            try {
                                ob.handler(ob);
                            }
                            catch (ex) {
                                console.warn('[sys-interval] error', ex);
                            }
                        }, 0);
                    }
                })
                    .then();
            }
        };
        const func = () => {
            element.eid = window.setTimeout(() => {
                console.log('[sys] interval run once ', element);
                handler();
                func();
            }, element.period);
        };
        return func;
    }
    cancelEvent(period, oid, disable) {
        if (typeof window === 'undefined' || typeof window.setInterval === 'undefined') {
            return false;
        }
        if (period && oid && disable === undefined) {
            console.log('[sys] cancel event ', oid, period);
            const element = SystemApiImpl.eventStore[period];
            let id;
            if (typeof oid === 'string') {
                id = element.eventKeys[oid];
            }
            else {
                id = oid;
            }
            const ob = element.events.getOb(id);
            if (ob) {
                element.events.removeOb(id);
                if (ob.id) {
                    delete element.eventKeys[ob.id];
                }
                if (element.eid > 0 && element.events.total === 0) {
                    window.clearInterval(element.eid);
                }
            }
        }
        if (period && disable !== undefined) {
            const element = SystemApiImpl.eventStore[period];
            element.disable = disable;
        }
        return true;
    }
    getKey() {
        if (SystemApiImpl.key && SystemApiImpl.key.length > 0) {
            return Promise.resolve(SystemApiImpl.key);
        }
        return this.storeApi
            .get(SystemApiImpl.defaultPassKey, globalStoreConfig)
            .then((rs) => {
            if (rs.suc) {
                return rs.data;
            }
            const key = this.generateKey(16);
            return this.storeApi.put(SystemApiImpl.defaultPassKey, key, globalStoreConfig).then(rs => {
                if (rs) {
                    throw Error(rs);
                }
                return key;
            });
        })
            .then((rs) => {
            SystemApiImpl.key = rs;
            return rs;
        });
    }
    getStartTimeSpan() {
        return new Date().getTime() - this.apiStartTime;
    }
    generateKey(len) {
        return config(len.toString(16), 'genRandomKey');
    }
    isNetworkAvailable() {
        return SystemApiImpl.networkFail < 5;
    }
    getNetworkFailIndex() {
        return SystemApiImpl.networkFail;
    }
    watchStorageChange(ev) {
        if (ev && ev.eventData && ev.eventStrData) {
            const changeKeyName = ev.eventStrData;
            const changeDataInfo = ev.eventData;
            const key1 = this.storeApi.getKey(SystemApiImpl.keyOfProxyOn);
            if (changeKeyName === key1) {
                SystemApiImpl.proxyOn = changeDataInfo.newValue === 'true';
            }
            const hostTypeKey = this.storeApi.getKey(SystemApiImpl.STORE_HOST_TYPE_KEY, SystemApiImpl.STORE_HOST_TYPE_CONFIG);
            if (changeKeyName === hostTypeKey) {
                SystemApiImpl.useDomesticHost = changeDataInfo.newValue === SystemApiImpl.hostTypes.domestic;
            }
        }
    }
    init() {
        console.time('init_load_js');
        SystemApiImpl.proxyOn = this.getProxyOn(true);
        this.updateHostType();
        this.storeApi.addWatchedKey(SystemApiImpl.keyOfProxyOn);
        if (inWindow()) {
            this.dataTracker.initLimit('pc_dailyActiveUser', 3, 3);
            this.dataTracker.initLimit('email_verification_failed', 3, 3);
            this.intervalEvent(this.dailyActiveApp);
        }
        this.eventApi.registerSysEventObserver('networkFail', {
            name: 'systemApiNetworkFailOb',
            func: this.watchNetworkFail.bind(this),
        });
        this.eventApi.registerSysEventObserver('storeChangeEvent', {
            name: 'systemStorageChangeOb',
            func: this.watchStorageChange.bind(this),
        });
        const isAddAccountPage = this.getIsAddAccountPage() || this.getIsAddPersonalSubAccountPage() || this.getIsAddSubAccountPage();
        if (inWindow() && !isAddAccountPage) {
            setTimeout(() => {
                this.doGetCookies(true).then();
            }, 7000);
            window.addEventListener('online', this.watchEventOnOffLine.bind(this));
            window.addEventListener('offline', this.watchEventOnOffLine.bind(this));
        }
        this.apiStartTime = new Date().getTime();
        return this.name;
    }
    disableDbWin4CurrPage() {
        if (inWindow() && window && window.bridgeApi && window.bridgeApi.master) {
            window.bridgeApi.master.forbiddenBbWin4CurrPage();
        }
    }
    setCurrentSessionName(sessionName, _account) {
        if (!sessionName)
            return;
        this.storeApi.putSync(currentSessionNameKey, sessionName, Object.assign(Object.assign({}, currentSessionStoreConfig), { _account }));
    }
    getCurrentSessionName(_account) {
        const storeInfo = this.storeApi.getSync(currentSessionNameKey, Object.assign(Object.assign({}, currentSessionStoreConfig), { _account }));
        if (storeInfo.suc && storeInfo.data) {
            return storeInfo.data;
        }
        return this.getUrlSearchValue('sessionName');
    }
    async getSessionCookieStore(_account) {
        try {
            const sessionName = this.getCurrentSessionName(_account);
            if (!sessionName) {
                return [];
            }
            const cookieStores = await window.electronLib.appManage.getSessionCookieStore({ sessionName });
            return cookieStores;
        }
        catch (ex) {
            console.error('getSessionCookieStore error', ex);
            return [];
        }
    }
    getUrlSearchValue(key) {
        if (window) {
            const { href } = window.location;
            const urlInfo = new URL(href);
            return urlInfo.searchParams.get(key);
        }
        return '';
    }
    async doGetCookies(refresh, _account) {
        var _a;
        const shouldRefresh = !_account ? refresh : !this.subAccountCookies[_account] || refresh;
        if (shouldRefresh && inWindow()) {
            if (process.env.BUILD_ISELECTRON) {
                const isAddAccountPage = this.getIsAddAccountPage();
                const sessionName = isAddAccountPage ? this.getCurrentSessionName() : undefined;
                const cookieStore = isAddAccountPage
                    ? await window.electronLib.appManage.getSessionCookieStore({ sessionName })
                    : _account
                        ? await this.getSessionCookieStore(_account)
                        : await window.electronLib.appManage.getCookieStore();
                cookieStore.forEach((it) => {
                    this.cookies[it.name] = it.value;
                });
            }
            else if (_account) {
                if (!this.subAccountCookies[_account]) {
                    this.subAccountCookies[_account] = {};
                }
                const _cookies = (_a = this.getCurrentUser(_account)) === null || _a === void 0 ? void 0 : _a.cookies;
                _cookies === null || _cookies === void 0 ? void 0 : _cookies.forEach(cookieItem => {
                    this.subAccountCookies[_account][cookieItem.name] = cookieItem.value;
                });
            }
            else {
                const string = window.document.cookie;
                let re;
                do {
                    try {
                        re = SystemApiImpl.cookiePattern.exec(string);
                        if (re) {
                            this.cookies[re[1]] = re[2];
                        }
                        else {
                            break;
                        }
                    }
                    catch (e) {
                        console.error('get cookie error:', e);
                        break;
                    }
                } while (re);
            }
        }
        return !_account ? this.cookies : this.subAccountCookies[_account] || {};
    }
    clearUserAuthCookie(reserveMsgCodeCookie, shouldDeleteAllCookies) {
        console.log('clear cookies in web,', reserveMsgCodeCookie, shouldDeleteAllCookies);
        document.cookie = '';
    }
    getAppName() {
        if (process.env.BUILD_ISEDM) {
            return process.env.BUILD_ISELECTRON ? 'sirius-desktop-waimao' : 'sirius-web-waimao';
        }
        return process.env.BUILD_ISELECTRON ? 'sirius-desktop' : 'sirius-web';
    }
    getSimpleDeviceInfo(shouldMd5) {
        const appName = this.getAppName();
        try {
            const deviceId = process.env.BUILD_ISELECTRON ? this.storeApi.getUUID() + '_' + appName + '_' + config('stage') : this.storeApi.getUUID();
            return {
                _appName: appName,
                _deviceId: shouldMd5 ? this.md5(deviceId) : deviceId,
            };
        }
        catch (ex) {
            return {
                _appName: appName,
                _deviceId: '',
            };
        }
    }
    async getDeviceInfo() {
        let deviceInfo;
        if (this.isElectron() && window.electronLib) {
            deviceInfo = await window.electronLib.appManage.getDeviceInfo();
            const simpleDeviceInfo = this.getSimpleDeviceInfo();
            deviceInfo._appName = simpleDeviceInfo._appName;
            deviceInfo._deviceId = simpleDeviceInfo._deviceId;
        }
        else {
            deviceInfo = this.getBrowserDeviceInfo();
        }
        deviceInfo._deviceId = this.md5(deviceInfo._deviceId || this.storeApi.getUUID());
        deviceInfo._version = ver;
        return deviceInfo;
    }
    navigateMainWindow(url, state = {}, replace = false) {
        try {
            if (!process.env.BUILD_ISELECTRON || !url || !url.length)
                return false;
            this.eventApi.sendSysEvent({
                eventName: 'mainWindowNavigate',
                eventData: {
                    url,
                    state,
                    replace,
                },
            });
            return true;
        }
        catch (ex) {
            console.error('navigateToMainWindow-catch-error', ex);
            return false;
        }
    }
    async getDeviceMacInfo() {
        if (process.env.BUILD_ISELECTRON) {
            const deviceInfo = await window.electronLib.appManage.getDeviceMacInfo();
            return deviceInfo;
        }
        return null;
    }
    getBrowserDeviceInfo() {
        var _a;
        const ua = window.navigator.userAgent.toLowerCase();
        const browserInfo = this.getBrowserInfo();
        const macReg = ua.match(/(mac os x)\s+([\w_]+)/i);
        const windowReg = ua.match(/(windows nt)\s+([\w.]+)/i);
        let _systemVersion = '';
        if (windowReg) {
            _systemVersion = windowReg[2];
        }
        else if (macReg) {
            _systemVersion = (_a = macReg[2]) === null || _a === void 0 ? void 0 : _a.replace(/_/g, '.');
        }
        const _system = 'web';
        const _manufacturer = browserInfo.name || '';
        const _device = browserInfo.name || 'browser';
        const _deviceName = browserInfo.name + ' ' + browserInfo.version;
        const _deviceId = this.storeApi.getUUID();
        return {
            p: 'web',
            _deviceId,
            _device,
            _systemVersion,
            _system,
            _manufacturer,
            _deviceName,
            _appName: this.getAppName(),
        };
    }
    getBrowserInfo() {
        const agent = navigator.userAgent.toLowerCase();
        let res = null;
        let name = 'unknown';
        let version = 'unknown';
        let weight = 0;
        Object.keys(this.browserAgentIdStr).forEach(it => {
            if (Object.prototype.hasOwnProperty.apply(this.browserAgentIdStr, [it])) {
                const [reg, w] = this.browserAgentIdStr[it];
                res = reg.exec(agent);
                if (res && res[1]) {
                    if (w >= weight) {
                        weight = w;
                        name = it;
                        version = res[1];
                    }
                }
            }
        });
        return {
            name,
            version,
        };
    }
    async navigateToSchedule(frameNavCallback) {
        const { scheduleTabOpenInWindow } = configKeyStore;
        const keyHolder = this.storeApi.getSync(scheduleTabOpenInWindow.keyStr);
        if (this.isElectron() && keyHolder && keyHolder.suc && keyHolder.data && keyHolder.data === 'true') {
            const scheduleWinInfo = await this.createWindow('schedule');
            return scheduleWinInfo;
        }
        frameNavCallback && frameNavCallback();
        return false;
    }
    switchLoading(bool) {
        const ckRes = checkMasterInfo();
        const judgeWay = (ckRes === null || ckRes === void 0 ? void 0 : ckRes.isMaster) || process.env.MOCK_ENV === 'mobile' ? 'header' : 'cookie';
        if (judgeWay === 'header') {
            return;
        }
        const loading = document.querySelector('#sirius-root-loading');
        if (!loading) {
            throw new Error('全局loading没了');
        }
        if (bool) {
            loading === null || loading === void 0 ? void 0 : loading.setAttribute('class', `sirius-loading ${this.inEdm() ? 'sirius-root-loading-edm' : ''}`);
        }
        else {
            console.timeEnd('init_load_js');
            loading === null || loading === void 0 ? void 0 : loading.removeAttribute('class');
        }
    }
    switchAppLoading(bool) {
        const loading = document.querySelector('#sirius-app-loading');
        if (!loading) {
            throw new Error('App loading没了');
        }
        if (bool) {
            loading === null || loading === void 0 ? void 0 : loading.setAttribute('class', 'sirius-app-loading show');
        }
        else {
            loading.setAttribute('class', 'sirius-app-loading');
        }
    }
    inWebMail() {
        const profile = buildProfile;
        return profile === null || profile === void 0 ? void 0 : profile.startsWith('webmail');
    }
    invalidate() {
        window.electronLib.windowManage.invalidate();
    }
    isFirstInit(bool) {
        if (bool !== undefined) {
            this.storeApi.putSync('isFirstInit', bool ? 'true' : 'false', {
                noneUserRelated: true,
            });
            return bool;
        }
        const { suc, data } = this.storeApi.getSync('isFirstInit', {
            noneUserRelated: true,
        });
        return !(suc && data && data === 'false');
    }
    rsaEncrypt(m, e, rand, con) {
        if (!m || m.length === 0 || !e || e.length === 0 || !con || con.length === 0) {
            throw new Error('加密参数不全[' + m + ' ' + e + ' ' + rand + ']');
        }
        return rsaEncrypt(m, e, rand, con);
    }
    getHasTrackEvent(key) {
        try {
            const data = this.storeApi.getSync(key, globalStoreConfig);
            if (data && data.data) {
                return true;
            }
            return false;
        }
        catch (ex) {
            console.error('getHasTrackEvent Error', ex);
            return false;
        }
    }
    setHasTrackEvent(key) {
        try {
            this.storeApi.putSync(key, 'true', globalStoreConfig);
        }
        catch (ex) {
            console.error('setHasTrackEvent-error', ex);
        }
    }
    async trackCpuAndMemInfo() {
        try {
            if (!inWindow() || !window.electronLib) {
                return;
            }
            const hasTracked = this.getHasTrackEvent(trackCpuMemKey);
            if (hasTracked)
                return;
            const cpuAndMemInfo = await window.electronLib.appManage.getCpuMemInfo();
            if (cpuAndMemInfo) {
                let cpuTracked = false;
                if (cpuAndMemInfo.cpu && cpuAndMemInfo.cpu.name) {
                    this.dataTracker.track('pcMail_cpu', {
                        cpuName: cpuAndMemInfo.cpu.name,
                        cpuNum: cpuAndMemInfo.cpu.coreNum,
                    });
                    cpuTracked = true;
                }
                let memTracked = false;
                if (cpuAndMemInfo.memory && cpuAndMemInfo.memory.total) {
                    this.dataTracker.track('pcMail_internalStorage', { detail: cpuAndMemInfo.memory.total });
                    memTracked = true;
                }
                if (cpuTracked && memTracked) {
                    this.setHasTrackEvent(trackCpuMemKey);
                }
            }
        }
        catch (ex) {
            console.error('trackCpuAndMemInfo error', ex);
        }
    }
    async trackWinInstall() {
        try {
            if (!inWindow() || !window.electronLib) {
                return;
            }
            if (window.electronLib.env.isMac) {
                return;
            }
            const currentVersion = window.electronLib.env.version;
            const WIN_INSTALL_SITE_KEY = 'WIN_INSTALL_KEY';
            const storeConfig = { noneUserRelated: true };
            const preTrackInfo = this.storeApi.getSync(WIN_INSTALL_SITE_KEY, storeConfig);
            if (preTrackInfo && preTrackInfo.data) {
                try {
                    const preTrackObj = JSON.parse(preTrackInfo.data);
                    if (preTrackObj[currentVersion]) {
                        return;
                    }
                }
                catch (ex) {
                    console.error(ex);
                }
            }
            const installPath = await window.electronLib.appManage.getPath('exe');
            if (installPath) {
                const installSite = installPath.substring(0, 1);
                this.dataTracker.track('win_installSite', { site: installSite });
                this.storeApi.putSync(WIN_INSTALL_SITE_KEY, JSON.stringify({
                    [currentVersion]: true,
                }), storeConfig);
            }
        }
        catch (ex) {
            console.error('trackWinInstall', ex);
        }
    }
    async trackMailCacheSize() {
        try {
            if (!inWindow() || !window.electronLib)
                return;
            const TRACK_CACHE_SIZE_KEY = 'TRACK_CACHE_SIZE_KEY';
            const storeConfig = { noneUserRelated: true };
            const isTrackToday = this.getIsTrackToday(TRACK_CACHE_SIZE_KEY, storeConfig);
            if (isTrackToday) {
                try {
                    const { isMac } = window.electronLib.env;
                    const userDataPath = await window.electronLib.appManage.getPath('userData');
                    const downloadUserPath = isMac ? userDataPath + '/download' : userDataPath + '\\download';
                    const folderSize = await this.getFolderSize(downloadUserPath);
                    if (!folderSize)
                        return;
                    const sizeMb = Number.parseFloat((folderSize / 1024).toFixed(2));
                    const TRACK_CACHE_COUNT_KEY = 'TRACK_CACHE_COUNT_KEY';
                    const trackCount = this.getUploadTimeByKey(TRACK_CACHE_COUNT_KEY, storeConfig);
                    this.dataTracker.track('cacheSize', {
                        cacheType: '所有附件',
                        sizeRange: this.getCacheRangeKey(sizeMb),
                        sizeDetail: (sizeMb / 1024).toFixed(2),
                        uploadTime: trackCount,
                    });
                    const dayKey = this.getStoreDayKey();
                    this.storeApi.putSync(TRACK_CACHE_COUNT_KEY, trackCount.toString(), storeConfig);
                    this.storeApi.putSync(TRACK_CACHE_SIZE_KEY, JSON.stringify({
                        [dayKey]: true,
                    }), storeConfig);
                }
                catch (ex) {
                    console.error(ex);
                }
            }
        }
        catch (ex) {
            console.error('trackMailCacheSize', ex);
        }
    }
    doGetProductInfo() {
        return this.productAuthApi.doGetProductInfo();
    }
    getStoreDayKey() {
        const now = new Date();
        const dayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
        return dayKey;
    }
    getIsTrackToday(storeKey, storeConfig) {
        const preStoreInfo = this.storeApi.getSync(storeKey, storeConfig);
        if (preStoreInfo && preStoreInfo.data) {
            try {
                const preStoreObj = JSON.parse(preStoreInfo.data);
                const dayKey = this.getStoreDayKey();
                if (preStoreObj[dayKey]) {
                    return false;
                }
                return true;
            }
            catch (ex) {
                console.error('getIsTrackToday', ex);
            }
        }
        return true;
    }
    getUploadTimeByKey(uploadKey, storeConfig) {
        const storeInfo = this.storeApi.getSync(uploadKey, storeConfig);
        let count = 1;
        if (storeInfo && storeInfo.data) {
            count = Number.parseInt(storeInfo.data, 10) + 1;
        }
        return count;
    }
    async trackDbSize() {
        try {
            if (!inWindow()) {
                return;
            }
            if (navigator && navigator.storage && navigator.storage.estimate) {
                const CACHE_DB_SIZE_KEY = 'CACHE_DB_SIZE_KEY';
                const storeConfig = { noneUserRelated: true };
                const isTrackToday = this.getIsTrackToday(CACHE_DB_SIZE_KEY, storeConfig);
                if (!isTrackToday)
                    return;
                const storageInfo = await navigator.storage.estimate();
                if (storageInfo && storageInfo.usage) {
                    const sizeMb = Number.parseFloat((storageInfo.usage / 1024 / 1024).toFixed(2));
                    const rangeKey = this.getCacheRangeKey(sizeMb);
                    const CACHE_DB_SIZE_COUNT_KEY = 'CACHE_DB_SIZE_COUNT_KEY';
                    const trackCount = this.getUploadTimeByKey(CACHE_DB_SIZE_COUNT_KEY, storeConfig);
                    this.dataTracker.track('cacheSize', {
                        cacheType: 'DB',
                        sizeRange: rangeKey,
                        sizeDetail: sizeMb,
                        uploadTime: trackCount,
                    });
                    const dayKey = this.getStoreDayKey();
                    this.storeApi.putSync(CACHE_DB_SIZE_COUNT_KEY, trackCount.toString(), storeConfig);
                    this.storeApi.putSync(CACHE_DB_SIZE_KEY, JSON.stringify({
                        [dayKey]: true,
                    }), storeConfig);
                }
            }
        }
        catch (ex) {
            console.error('trackDbSize error', ex);
        }
    }
    async getFolderSize(folderPath) {
        if (!inWindow() || !window.electronLib) {
            return;
        }
        const { isMac } = window.electronLib.env;
        if (!isMac)
            return;
        if (!folderPath)
            return;
        const { appManage } = window.electronLib;
        const folderSizeArr = await appManage.getFolderSize(folderPath);
        if (!folderSizeArr || !folderSizeArr.length)
            return;
        if (isMac) {
            const totalItems = folderSizeArr.filter(item => item.dir && item.dir === '.');
            const totalItem = totalItems && totalItems.length ? totalItems[0] : null;
            if (!totalItem)
                return;
            return totalItem.size;
        }
        const winSizeItem = folderSizeArr[0];
        if (!winSizeItem)
            return;
        return winSizeItem.size;
    }
    async getAndCheckWatermark(sendEvent) {
        try {
            const res = await this.accountApi.doGetMarkInfo();
            if (!res)
                return;
            const { orgOpen, markEnable, scopeHit, apps, markDetail, color, density, densityBase } = res;
            let show = false;
            if (orgOpen && markEnable && scopeHit && apps && apps.includes('sirius') && markDetail && color && density && densityBase) {
                show = true;
            }
            const managedData = show
                ? {
                    show,
                    markDetail,
                    color,
                    density,
                    densityBase,
                }
                : { show };
            if (!sendEvent) {
                SystemApiImpl.watermarkData = managedData;
                return managedData;
            }
            const { watermarkData: curData } = SystemApiImpl;
            let dataChanged = false;
            for (const key in managedData) {
                if (curData.hasOwnProperty(key)) {
                    if (managedData[key] !== curData[key]) {
                        dataChanged = true;
                    }
                }
                else {
                    dataChanged = true;
                }
            }
            if (dataChanged) {
                SystemApiImpl.watermarkData = managedData;
                this.eventApi.sendSysEvent({
                    eventName: 'watermarkChanged',
                    eventData: managedData,
                });
            }
            return managedData;
        }
        catch (error) {
            console.error('intervalGetWatermark error', error);
        }
    }
    trackResourceLoadTime() {
        try {
            if (!process.env.BUILD_ISEDM) {
                return;
            }
            const entities = performance.getEntries();
            const newEntities = entities.filter(item => item.fetchStart && item.fetchStart > SystemApiImpl.lastReportTime);
            if (newEntities && newEntities.length) {
                SystemApiImpl.lastReportTime = newEntities[newEntities.length - 1].fetchStart;
                newEntities.forEach(item => {
                    if (item.initiatorType === 'xmlhttprequest') {
                        if (item.duration > 5 * 1000) {
                            const props = {
                                url: getUrlPathByString(item.name),
                                value: Math.round(item.duration),
                            };
                            if (item.duration > 10 * 1000) {
                                this.dataTracker.track('xmlhttprequest_more_10', props);
                            }
                            else if (item.duration > 5 * 1000) {
                                this.dataTracker.track('xmlhttprequest_more_5', props);
                            }
                        }
                    }
                });
            }
            performance.clearMarks();
            performance.clearMeasures();
            performance.clearResourceTimings();
        }
        catch (ex) {
            console.error('trackResourceLoadTime catch', ex);
        }
    }
    startTrackResourceLoadTime() {
        if (!process.env.BUILD_ISEDM) {
            return;
        }
        performance.setResourceTimingBufferSize(1500);
        setTimeout(() => {
            this.trackResourceLoadTime();
        }, 20 * 1000);
        setTimeout(() => {
            this.intervalEvent({
                seq: 0,
                id: 'intervalTrackResourceLoadTime',
                eventPeriod: 'long',
                handler: ev => {
                    if (ev.seq % 2 !== 0 || ev.seq === 0) {
                        return;
                    }
                    this.trackResourceLoadTime();
                },
            });
        }, 3000);
    }
    afterLoadFinish() {
        if (!inWindow())
            return this.name;
        const isMainPage = this.isMainPage();
        this.startTrackResourceLoadTime();
        if (isMainPage) {
            setTimeout(() => {
                this.trackCpuAndMemInfo();
                this.trackWinInstall();
                this.trackMailCacheSize();
                this.trackDbSize();
            }, 30000);
            const isLowMemoryMode = this.getIsLowMemoryModeSync();
            if (!isLowMemoryMode) {
                this.createBkStableWindow();
            }
            setTimeout(() => {
                this.sendWindowCrashInfo();
                this.sendMainCrashInfo();
            }, 15000);
            const currentUser = this.getCurrentUser();
            if (currentUser) {
                this.addCrashReportParams({
                    key: 'currentEmail',
                    value: currentUser.id,
                });
            }
            if (currentUser) {
                setTimeout(() => {
                    this.intervalEvent({
                        seq: 0,
                        id: 'intervalGetWatermark',
                        eventPeriod: 'extLong',
                        handler: ev => {
                            if (ev.seq % 6 !== 0 || ev.seq === 0) {
                                return;
                            }
                            this.getAndCheckWatermark(true);
                        },
                    });
                }, 5000);
            }
        }
        return this.name;
    }
    async createBkStableWindow() {
        if (process.env.BUILD_ISELECTRON) {
            const bkStableType = 'bkStable';
            const allWinInfo = await window.electronLib.windowManage.getAllWinInfo();
            const hasBkStable = allWinInfo.some(item => item.type === bkStableType);
            if (!hasBkStable) {
                window.electronLib.windowManage.createWindow({ type: bkStableType, manualShow: true }).then(res => {
                    console.log('!!! -- build app init window success -- !!!');
                    return res;
                });
            }
        }
    }
    getIsLowMemoryModeSync() {
        return SystemApiImpl.isLowMemoryMode;
    }
    getCurrentCompanyId(email) {
        const user = this.getCurrentUser(email);
        const tempCompanyId = lodashGet(user, 'contact.contact.enterpriseId', 0) || lodashGet(user, 'prop.companyId', '').replace(/[^\d]+/, '');
        return !Number.isNaN(tempCompanyId) ? Number(tempCompanyId) : 0;
    }
    async getIsLowMemoryMode() {
        const defaultValue = true;
        if (!inWindow()) {
            return defaultValue;
        }
        if (window && window.electronLib) {
            try {
                if (process.env.BUILD_ISEDM) {
                    return true;
                }
                const res = await window.electronLib.storeManage.get('user', LOW_MEMORY_MODE_KEY);
                return res;
            }
            catch (ex) {
                console.error('getIsLowMemoryMode', ex);
                return defaultValue;
            }
        }
        return defaultValue;
    }
    async setSystemProxyType(value) {
        try {
            if (!inWindow())
                return;
            if (process.env.BUILD_ISELECTRON) {
                SystemApiImpl.systemProxyType = value;
                await window.electronLib.storeManage.set('user', SYSTEM_PROXY_TYPE_STORE_KEY, value);
            }
        }
        catch (ex) {
            console.error('setUseSystemProxyType error', ex);
        }
    }
    async setIsLowMemoryMode(value) {
        try {
            if (!inWindow()) {
                return;
            }
            if (window && window.electronLib) {
                SystemApiImpl.isLowMemoryMode = value;
                await window.electronLib.storeManage.set('user', LOW_MEMORY_MODE_KEY, value);
                if (value) {
                    this.disableDbWin4CurrPage();
                }
            }
        }
        catch (ex) {
            console.error('setIsLowMemoryMode', ex);
        }
    }
    getSystemLang() {
        if (!inWindow()) {
            return DEFAULT_LANG;
        }
        try {
            if (window.systemLang) {
                return window.systemLang;
            }
            const langStr = localStorage.getItem(SYSTEM_LANGUAGE);
            if (langStr) {
                window.systemLang = langStr;
            }
            return window.systemLang || DEFAULT_LANG;
        }
        catch (ex) {
            console.error('getSystemLang error', ex);
            return DEFAULT_LANG;
        }
    }
    setSystemLang(val) {
        try {
            if (localStorage) {
                localStorage.setItem(SYSTEM_LANGUAGE, val);
                return true;
            }
            return false;
        }
        catch (ex) {
            console.error('setSystemLang error', ex);
            return false;
        }
    }
    setLockApp(val) {
        try {
            this.storeApi.putSync(APP_LOCK_KEY, val, globalStoreConfig);
            this.isLockingApp = val === 'true';
            this.eventApi.sendSysEvent({
                eventName: 'onAppLockChanged',
                eventData: {
                    isLockApp: val === 'true',
                },
            });
            return true;
        }
        catch (ex) {
            console.error('setLockApp error to ' + val, ex);
            return false;
        }
    }
    getIsLockApp() {
        const default_val = false;
        try {
            if (!process.env.BUILD_ISELECTRON)
                return false;
            if (this.isLockingApp !== null) {
                return this.isLockingApp;
            }
            const data = this.storeApi.getSync(APP_LOCK_KEY, globalStoreConfig);
            if (data && data.suc && data.data) {
                const res = data.data === 'true';
                this.isLockingApp = res;
                return res;
            }
            return default_val;
        }
        catch (ex) {
            console.error('getIsLockApp error', ex);
            return default_val;
        }
    }
    lockApp() {
        if (process.env.BUILD_ISELECTRON) {
            window.electronLib.windowManage.getAllWinInfo().then(winInfos => {
                if (winInfos && winInfos.length) {
                    const blackWinTypes = ['main', 'bkStable'];
                    winInfos
                        .filter(winInfo => !blackWinTypes.includes(winInfo.type))
                        .forEach(winInfo => {
                        this.closeSubWindow(winInfo.id, false, true);
                    });
                }
            });
        }
        return this.setLockApp('true');
    }
    unLockApp() {
        if (!process.env.BUILD_ISELECTRON) {
            return false;
        }
        return this.setLockApp('false');
    }
    async getIsAutoLaunchToTray() {
        const defaultRes = false;
        try {
            if (!process.env.BUILD_ISELECTRON) {
                return defaultRes;
            }
            const res = await window.electronLib.storeManage.get('app', 'autoLaunchToTray');
            return (res && res.toString() === 'true') || defaultRes;
        }
        catch (ex) {
            console.log('getIsAutoLaunchToTray-catch', ex);
            return defaultRes;
        }
    }
    async setIsAutoLaunchToTray(val) {
        try {
            if (!process.env.BUILD_ISELECTRON) {
                return {
                    success: true,
                };
            }
            await window.electronLib.appManage.setAppAutoLaunchToTray(val);
            return {
                success: true,
            };
        }
        catch (ex) {
            return {
                success: false,
                errorMsg: (ex && ex.message) || '',
            };
        }
    }
    async getPageZoomValue() {
        const defaultValue = -1;
        try {
            if (!process.env.BUILD_ISELECTRON) {
                return defaultValue;
            }
            const val = await window.electronLib.storeManage.get('app', ZOOM_VAL_KEY);
            return Number(val);
        }
        catch (ex) {
            return defaultValue;
        }
    }
    async setPageZoomValue(val) {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return true;
            await window.electronLib.storeManage.set('app', ZOOM_VAL_KEY, val);
            await window.electronLib.windowManage.setMainWindowZoomFactor(val);
            return true;
        }
        catch (ex) {
            console.error('setPageZoomValue-catch', ex);
            return false;
        }
    }
    getActions(params) {
        var _a;
        const { actions, subActions, _account } = params;
        if (!_account) {
            return { suc: true, val: actions };
        }
        const targetAccountId = this.accountApi.getEmailIdByEmail(_account);
        if (targetAccountId === '') {
            return { suc: true, val: actions };
        }
        const mainAccountId = (_a = this.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id;
        if (targetAccountId === mainAccountId) {
            return { suc: true, val: actions };
        }
        const targetActions = subActions === null || subActions === void 0 ? void 0 : subActions.get(targetAccountId);
        if (targetActions) {
            return { suc: true, val: targetActions };
        }
        return { suc: false, val: null, msg: 'account not existed' };
    }
    getSessionNameOfSubAccount(subEmailId) {
        const currentUser = this.getCurrentUser();
        const mainAccountEmail = currentUser ? currentUser.id : '';
        if (!mainAccountEmail) {
            return '';
        }
        const subAccountEmailId = this.storeApi.getEmailIdByEmail(subEmailId);
        if (!subAccountEmailId) {
            return '';
        }
        return `persist:${mainAccountEmail}-${subAccountEmailId}`;
    }
    getIsThirdSubAccountByEmailId(emailId) {
        return !!(emailId && emailId.includes('.third.'));
    }
    sendMainCrashToOxpecker(reportInfo) {
        if (!reportInfo || !reportInfo.id)
            return;
        const key = 'MAIN_CRASH_REPORT';
        const alreadyReportInfo = this.storeApi.getSync(key, globalStoreConfig);
        let alreadyReportMap = {};
        if (alreadyReportInfo && alreadyReportInfo.data) {
            try {
                alreadyReportMap = JSON.parse(alreadyReportInfo.data);
                if (alreadyReportMap[reportInfo.id]) {
                    return;
                }
            }
            catch (ex) {
                console.error('parse error', ex);
            }
        }
        this.dataTracker.track('electron_main_crash', {
            crashDate: reportInfo.date && typeof reportInfo.date.getTime === 'function' ? reportInfo.date.getTime() : '',
            crashId: reportInfo.id,
        });
        alreadyReportMap[reportInfo.id] = true;
        this.storeApi.putSync(key, JSON.stringify(alreadyReportMap), globalStoreConfig);
    }
    async sendMainCrashInfo() {
        if (!process.env.BUILD_ISELECTRON)
            return;
        try {
            const isMainWindow = this.isMainWindow();
            if (!isMainWindow)
                return;
            const reportInfos = await window.electronLib.appManage.getUploadedReports();
            if (!reportInfos || !reportInfos.length)
                return;
            reportInfos.forEach(reportInfo => {
                this.sendMainCrashToOxpecker(reportInfo);
            });
        }
        catch (ex) {
            console.error('sendMainCrashInfo-catch', ex);
        }
    }
    async sendWindowCrashInfo() {
        if (!process.env.BUILD_ISELECTRON)
            return;
        try {
            const isMainWindow = this.isMainWindow();
            if (!isMainWindow)
                return;
            const crashInfos = await window.electronLib.appManage.getWindowCrashInfos();
            if (!crashInfos || !crashInfos.length)
                return;
            crashInfos.forEach(crashInfo => {
                this.dataTracker.track('electron_window_crash', Object.assign({}, (crashInfo || {})));
            });
            await window.electronLib.appManage.deleteWinCrashInfos();
        }
        catch (ex) {
            console.error('sendWindowCrashInfo-catch', ex);
        }
    }
    getWaimaoHost() {
        const profile = buildProfile;
        const stage = config('stage');
        const waimaoHost = {
            dev: 'https://waimao-dev.cowork.netease.com/',
            test: 'https://waimao.cowork.netease.com/',
            prod: 'https://waimao.office.163.com/',
            prev: 'https://waimao-pre.office.163.com/',
        };
        if (stage === 'prod' && profile.includes('test_prod')) {
            return this.httpApi.getLxTrafficLabel() === 'pre' ? waimaoHost.prev : waimaoHost.prod;
        }
        return waimaoHost[stage] || waimaoHost.prod;
    }
    getJumpUrl(code, target) {
        var _a;
        const sid = (_a = this.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.sessionId;
        if (!sid) {
            return '';
        }
        const query = `jumpMode=clientLogin&sid=${sid}&target=${encodeURIComponent(target)}`;
        const host = this.getWaimaoHost();
        const redirectUrl = host + encodeURIComponent(`jump/?${query}`);
        const path = 'it-others/api/pub/login/jump/';
        return `${host}${path}?code=${code}&redirectUrl=${redirectUrl}`;
    }
    async openWaimaoUrlWithLoginInfo(url, needLoginInfo) {
        if (!needLoginInfo && (!this.isElectron() || !process.env.BUILD_ISEDM)) {
            window.open(url, '_blank');
            return;
        }
        this.loginApi
            .getLoginCode()
            .then(code => {
            const jumpUrl = this.getJumpUrl(code, url);
            window.open(jumpUrl, '_blank');
        })
            .catch(e => {
            console.error('openUrlInWebWithLoginInfo error', e);
            window.open(url, '_blank');
        });
    }
    openMarketingTaskPage(path, initPageData) {
        const queryString = path.split('?')[1];
        if (this.isElectron()) {
            const query = new URLSearchParams(queryString);
            const params = {};
            for (const [key, value] of query) {
                params[key] = value;
            }
            this.createWindowWithInitData({ type: 'marketingTaskPage', additionalParams: params }, {
                eventName: 'initPage',
                eventData: initPageData || {},
            });
        }
        else {
            if (initPageData === null || initPageData === void 0 ? void 0 : initPageData.templateContent) {
                localStorage.setItem('marketingTaskPagetemplateContent', JSON.stringify(initPageData === null || initPageData === void 0 ? void 0 : initPageData.templateContent));
            }
            window.open(`${systemApi.getContextPath()}/marketingTaskPage/#${this.isWebWmEntry() ? 'intelliMarketing' : 'edm'}?${queryString}`);
        }
    }
    updateMainPageUrl() {
        if (!inWindow())
            return;
        const isMainPage = this.isMainPage();
        if (!isMainPage)
            return;
        const hash = location.href;
        if (!hash)
            return;
        this.addCrashReportParams({
            key: 'currentUrl',
            value: location.href,
        });
    }
    addCrashReportParams(options) {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return;
            window.electronLib.appManage.setCrashReporterParams(options);
        }
        catch (ex) {
            console.error('addCrashReportParams-error', ex);
        }
    }
    async setAppStorageByKey(key, value) {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return false;
            await window.electronLib.storeManage.set('app', key, value);
            return true;
        }
        catch (ex) {
            console.error('setDisableAccessibility-catch-ex', ex);
            return false;
        }
    }
    async getDisableAccessibility() {
        try {
            if (!process.env.BUILD_ISELECTRON)
                return false;
            const disableByUser = await window.electronLib.storeManage.get('app', DISABLE_ACCESS_KEY);
            if (disableByUser) {
                return !!(disableByUser === 'true' || disableByUser === 'null');
            }
            return true;
        }
        catch (ex) {
            return false;
        }
    }
    async setDisableAccessibility(disable) {
        return this.setAppStorageByKey(DISABLE_ACCESS_KEY, disable.toString() || 'null');
    }
    toggleMainWindowMsg(url, eventStrData, eventData) {
        try {
            if (!process.env.BUILD_ISELECTRON || !url || !url.length)
                return false;
            this.eventApi.sendSysEvent({
                eventName: 'mainWindowToggleMsg',
                eventData,
                eventStrData,
            });
            return true;
        }
        catch (ex) {
            console.error('mainWindowToggleMsg-catch-error', ex);
            return false;
        }
    }
    async RSAEncryptData(hexKey, content) {
        try {
            const signResult = await RSASign(hexKey, content);
            return arrayBufferToHex(signResult);
        }
        catch (error) {
            console.error('[system_impl] RSAEncryptData error', error);
            return Promise.reject(error);
        }
    }
    getIsBuildLingHang() {
        return process.env.BUILD_ISLINGHANG;
    }
}
SystemApiImpl.sysNotificationTags = {
    mail: 'mail',
    im: 'im',
    sys: 'sys',
    whatsApp: 'whatsApp',
    facebook: 'facebook',
};
SystemApiImpl.defaultTag = 'sys';
SystemApiImpl.host = config('host');
SystemApiImpl.hostForTest = config('diskHost');
SystemApiImpl.contextPath = config('contextPath');
SystemApiImpl.loginPage = (config('host') + config('loginPage'));
SystemApiImpl.mloginPage = (config('host') + config('mloginPage'));
SystemApiImpl.resourceRouterConfig = ['sheet', 'doc', 'share', 'unitable'];
SystemApiImpl.defaultPassKey = config('globalKey');
SystemApiImpl.defaultSalt = config('globalSalt');
SystemApiImpl.corpConf = config('mail_mode_corpMail');
SystemApiImpl.domesticHost = isPord ? config('domesticHost') : '';
SystemApiImpl.webMailHZHost = config('webMailHZHost');
SystemApiImpl.webMailBJHost = config('webMailBJHost');
SystemApiImpl.lastReportTime = 0;
SystemApiImpl.isLowMemoryMode = isLowMemoryMode;
SystemApiImpl.systemProxyType = systemProxyType;
SystemApiImpl.hostTypes = {
    domestic: 'domestic',
    smartDNSHost: 'smartDNSHost',
};
SystemApiImpl.watermarkData = {
    show: false,
};
SystemApiImpl.hostTypeToUrlMap = {
    domestic: 'https://lingxi-domestic.office.163.com/login/',
    smartDNSHost: 'https://lingxi.office.163.com/login/',
};
SystemApiImpl.emdHostTypeToUrlMap = {
    domestic: 'https://waimao-domestic.office.163.com/login/',
    smartDNSHost: 'https://waimao.office.163.com/login/',
};
SystemApiImpl.STORE_HOST_TYPE_KEY = 'host_type';
SystemApiImpl.STORE_HOST_TYPE_CONFIG = { noneUserRelated: true };
SystemApiImpl.useDomesticHost = false;
SystemApiImpl.charSeq = config('globalSeq').join('');
SystemApiImpl.domain = config('domain');
SystemApiImpl.msgCodeCookieName = config('msgCodeCookieName');
SystemApiImpl.key = '';
SystemApiImpl.eventStore = {
    extLong: {
        events: new ListStore(),
        eventKeys: {},
        eid: -1,
        period: 600000,
    },
    long: {
        events: new ListStore(),
        eventKeys: {},
        eid: -1,
        period: 90000,
    },
    mid: {
        events: new ListStore(),
        eventKeys: {},
        eid: -1,
        period: 15000,
    },
    short: {
        events: new ListStore(),
        eventKeys: {},
        eid: -1,
        period: 1000,
    },
};
SystemApiImpl.networkFail = 0;
SystemApiImpl.accountPattern = emailPattern;
SystemApiImpl.cookiePattern = /([a-zA-Z_\-.]+)=([^;]+)(;|$)/gi;
SystemApiImpl.keyOfProxyOn = 'oneSiteProxyOn';
SystemApiImpl.proxyOn = false;
SystemApiImpl.eventTrans = new Map([
    ['onAfterClose', 'electronClosed'],
    ['onBeforeClose', 'electronClose'],
    ['onActive', 'electronActive'],
    ['onBlur', 'electronBlur'],
    ['onShow', 'electronShow'],
    ['onHide', 'electronHide'],
    ['onLockScreen', 'lockScreen'],
    ['onUnlockScreen', 'unlockScreen'],
    ['onLaptopSuspend', 'laptopSuspend'],
    ['onLaptopResume', 'laptopResume'],
    ['onWillNavigate', 'willNavigate'],
]);
const systemApi = new SystemApiImpl();
api.registerSystemApi(systemApi);
export default systemApi;
